import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { driver } from 'driver.js';
import 'driver.js/dist/driver.css';
import './dashboardTour.css';
import { fetchTourProgress, saveTourProgress } from './tourProgressApi';
import {
  TOUR_AUTH_LOGOUT_EVENT,
  forceRemoveDriverDom,
  mountTourHandoffVeil,
  removeTourHandoffVeil,
} from './tourLifecycle';

export { TOUR_AUTH_LOGOUT_EVENT, forceRemoveDriverDom } from './tourLifecycle';

export const DASHBOARD_TOUR_ID = 'dashboard-overview';
export const DASHBOARD_TOUR_VERSION = 20;
export const TOUR_MODE_OVERVIEW = 'overview';
export const TOUR_MODE_EXTENDED = 'extended';
export const TOUR_MODE_SECTION = 'section';

/** Help-button menu: start overview or jump into a page walkthrough. */
export const TOUR_START_OPTIONS = [
  {
    id: 'overview',
    label: 'Overview',
    description: 'Quick tour of the dashboard home',
    mode: TOUR_MODE_OVERVIEW,
  },
  {
    id: 'assets',
    label: 'Assets',
    description: 'Inventory search, table, and detail',
    mode: TOUR_MODE_SECTION,
    startPage: 'targets',
    startElement: '[data-tour="nav-targets"]',
  },
  {
    id: 'scans-web',
    label: 'Web scans',
    description: 'Filters, search, bulk actions, and history',
    mode: TOUR_MODE_SECTION,
    startPage: 'scansWeb',
    startElement: '[data-tour="nav-scans"]',
  },
  {
    id: 'scans-network',
    label: 'Network',
    description: 'Scopes, filters, and network scan history',
    mode: TOUR_MODE_SECTION,
    startPage: 'scansNetwork',
    startElement: '[data-tour="nav-scans-network"]',
  },
  {
    id: 'vulnerabilities',
    label: 'Vulnerabilities',
    description: 'Filters, list, detail, and reports',
    mode: TOUR_MODE_SECTION,
    startPage: 'risks',
    startElement: '[data-tour="nav-risks"]',
  },
  {
    id: 'reports',
    label: 'Reports',
    description: 'Filters, wizard, list, detail tabs, and exports',
    mode: TOUR_MODE_SECTION,
    startPage: 'reports',
    startElement: '[data-tour="nav-reports"]',
  },
  {
    id: 'settings',
    label: 'Settings',
    description: 'Connectors, deployment, users, organizations, and partners',
    mode: TOUR_MODE_SECTION,
    startPage: 'settingsConnectors',
    startElement: '[data-tour="nav-settings"]',
  },
  {
    id: 'subscription',
    label: 'Subscription',
    description: 'Organizations, plan and usage, billing, invoices, and add-ons',
    mode: TOUR_MODE_SECTION,
    startPage: 'subscription',
    startElement: '[data-tour="nav-subscription"]',
  },
  {
    id: 'support',
    label: 'Support',
    description: 'Tickets, conversation, and new requests',
    mode: TOUR_MODE_SECTION,
    startPage: 'support',
    startElement: '[data-tour="nav-support"]',
  },
];

/** Ordered page tours after Overview. Each runs alone with its own step count. */
export const TOUR_SECTION_DEFS = [
  {
    id: 'assets',
    label: 'Assets',
    pages: ['targets'],
    startPage: 'targets',
    startElement: '[data-tour="nav-targets"]',
  },
  {
    id: 'scans-web',
    label: 'Web scans',
    pages: ['scansWeb'],
    startPage: 'scansWeb',
    startElement: '[data-tour="nav-scans"]',
  },
  {
    id: 'scans-network',
    label: 'Network',
    pages: ['scansNetwork', 'scansPerimeter'],
    startPage: 'scansNetwork',
    startElement: '[data-tour="nav-scans-network"]',
  },
  {
    id: 'vulnerabilities',
    label: 'Vulnerabilities',
    pages: ['risks', 'riskDetail'],
    startPage: 'risks',
    startElement: '[data-tour="nav-risks"]',
  },
  {
    id: 'reports',
    label: 'Reports',
    pages: ['reports', 'reportDetail'],
    startPage: 'reports',
    startElement: '[data-tour="nav-reports"]',
  },
  {
    id: 'settings',
    label: 'Settings',
    pages: [
      'settingsConnectors',
      'settingsDeployment',
      'settingsManageUsers',
      'settingsUsersOrgs',
      'settingsPartners',
    ],
    startPage: 'settingsConnectors',
    startElement: '[data-tour="nav-settings"]',
    desktopOnly: true,
  },
  {
    id: 'subscription',
    label: 'Subscription',
    pages: ['subscription', 'subscriptionOrgDetail'],
    startPage: 'subscription',
    startElement: '[data-tour="nav-subscription"]',
    desktopOnly: true,
  },
  {
    id: 'support',
    label: 'Support',
    pages: ['support'],
    startPage: 'support',
    startElement: '[data-tour="nav-support"]',
  },
];

export function listTourSections({ isNarrowViewport = false } = {}) {
  return TOUR_SECTION_DEFS.filter((section) => !(section.desktopOnly && isNarrowViewport));
}

export function getTourSectionDef(sectionId) {
  return TOUR_SECTION_DEFS.find((section) => section.id === sectionId) || null;
}

export function getNextTourSectionId(sectionId, { isNarrowViewport = false } = {}) {
  const sections = listTourSections({ isNarrowViewport });
  const index = sections.findIndex((section) => section.id === sectionId);
  if (index < 0 || index >= sections.length - 1) return null;
  return sections[index + 1].id;
}

export function getFirstTourSectionId({ isNarrowViewport = false } = {}) {
  return listTourSections({ isNarrowViewport })[0]?.id || null;
}

function normalizeTourMode(mode) {
  if (mode === TOUR_MODE_SECTION || mode === TOUR_MODE_EXTENDED) return mode;
  return TOUR_MODE_OVERVIEW;
}

function uniqIds(values) {
  const out = [];
  const seen = new Set();
  (Array.isArray(values) ? values : []).forEach((value) => {
    const id = String(value || '').trim();
    if (!id || seen.has(id)) return;
    seen.add(id);
    out.push(id);
  });
  return out;
}

function normalizeIdList(values) {
  return uniqIds(values);
}

export function resolveTourSectionIdForPage(activePage, { scansPageTab = 'web' } = {}) {
  const page = String(activePage || '');
  if (page === 'dashboard') return 'overview';
  if (page === 'targets') return 'assets';
  if (page === 'scans') {
    return String(scansPageTab || '').toLowerCase() === 'network' ? 'scans-network' : 'scans-web';
  }
  if (page === 'risks' || page === 'riskDetail') return 'vulnerabilities';
  if (page === 'reports' || page === 'reportDetail') return 'reports';
  if (
    page === 'settingsConnectors' ||
    page === 'settingsDeployment' ||
    page === 'settingsManageUsers' ||
    page === 'settingsUsersOrgs' ||
    page === 'settingsPartners'
  ) {
    return 'settings';
  }
  if (page === 'subscription' || page === 'subscriptionOrgDetail') return 'subscription';
  if (page === 'support') return 'support';
  return null;
}

export function isTourOptionCompleted(progress, optionId) {
  if (!optionId) return false;
  return normalizeIdList(progress?.completedIds).includes(optionId);
}

export function isTourPromptDismissed(progress, optionId) {
  if (!optionId) return false;
  return normalizeIdList(progress?.promptDismissedIds).includes(optionId);
}

export function shouldOfferPageTourPrompt(progress, optionId) {
  if (!optionId || optionId === 'overview') return false;
  if (isTourOptionCompleted(progress, optionId)) return false;
  if (isTourPromptDismissed(progress, optionId)) return false;
  // A chained section start is already in flight — don't steal focus with the prompt.
  if (
    progress?.status === 'in_progress' ||
    progress?.status === 'section_in_progress' ||
    progress?.status === 'extended_in_progress'
  ) {
    return false;
  }
  return true;
}

function isDeepTourStep(step) {
  return Boolean(
    step?.requiresOpenAssetDetail ||
      step?.requiresOpenRiskDetail ||
      step?.requiresOpenReportDetail ||
      step?.requiresReportWizard ||
      step?.requiresConnectorModal ||
      step?.requiresOpenSubscriptionDetail ||
      step?.requiresAttemptDetail
  );
}

const TOUR_ACTION_OPEN_FIRST_ASSET = 'open-first-asset';
const TOUR_ACTION_CLOSE_ASSET_DETAIL = 'close-asset-detail';
const TOUR_ACTION_SELECT_FIRST_ASSET = 'select-first-asset';
const TOUR_ACTION_CLEAR_ASSET_SELECTION = 'clear-asset-selection';
const TOUR_ACTION_SELECT_FIRST_SCAN = 'select-first-scan';
const TOUR_ACTION_CLEAR_SCAN_SELECTION = 'clear-scan-selection';
const TOUR_ACTION_SELECT_FIRST_RISK = 'select-first-risk';
const TOUR_ACTION_CLEAR_RISK_SELECTION = 'clear-risk-selection';
const TOUR_ACTION_OPEN_FIRST_RISK = 'open-first-risk';
const TOUR_ACTION_CLOSE_RISK_DETAIL = 'close-risk-detail';
const TOUR_ACTION_NETWORK_SCOPE_ONPREM = 'network-scope-onprem';
const TOUR_ACTION_NETWORK_SCOPE_PERIMETER = 'network-scope-perimeter';
const TOUR_ACTION_NETWORK_SCOPE_SCHEDULED = 'network-scope-scheduled';
const TOUR_ACTION_OPEN_FIRST_REPORT = 'open-first-report';
const TOUR_ACTION_CLOSE_REPORT_DETAIL = 'close-report-detail';
const TOUR_ACTION_OPEN_REPORT_WIZARD = 'open-report-wizard';
const TOUR_ACTION_CLOSE_REPORT_WIZARD = 'close-report-wizard';
const TOUR_ACTION_REPORT_TAB_PREFIX = 'report-tab-';
const TOUR_ACTION_CONNECTORS_TAB_PREFIX = 'connectors-tab-';
const TOUR_ACTION_DEPLOYMENT_TAB_PREFIX = 'deployment-tab-';
const TOUR_ACTION_OPEN_CONNECTOR_CONFIG = 'open-connector-config';
const TOUR_ACTION_OPEN_NETWORK_CONNECTOR_CONFIG = 'open-network-connector-config';
const TOUR_ACTION_CLOSE_CONNECTOR_CONFIG = 'close-connector-config';
const TOUR_ACTION_SELECT_FIRST_ATTEMPT = 'select-first-attempt';
const TOUR_ACTION_OPEN_FIRST_SUBSCRIPTION_ORG = 'open-first-subscription-org';
const TOUR_ACTION_CLOSE_SUBSCRIPTION_ORG = 'close-subscription-org';
const TOUR_ACTION_SUBSCRIPTION_TAB_PREFIX = 'subscription-tab-';
const TOUR_ACTION_SUPPORT_TAB_PREFIX = 'support-tab-';
const TOUR_ACTION_SELECT_FIRST_TICKET = 'select-first-ticket';
const TOUR_ACTION_OPEN_NEW_SCAN = 'open-new-scan';
const TOUR_ACTION_CLOSE_NEW_SCAN = 'close-new-scan';
const TOUR_ACTION_OPEN_PROFILE_MENU = 'open-profile-menu';
const TOUR_ACTION_CLOSE_PROFILE_MENU = 'close-profile-menu';
const TOUR_ACTION_PROFILE_TAB_PREFIX = 'profile-tab-';

function normalizeIdentity(identity) {
  const value = String(identity || 'browser-user').trim().toLowerCase();
  return encodeURIComponent(value || 'browser-user');
}

export function getDashboardTourStorageKey(identity) {
  return `xspectre:tour:${DASHBOARD_TOUR_ID}:v${DASHBOARD_TOUR_VERSION}:${normalizeIdentity(identity)}`;
}

export function readDashboardTourProgress(storageKey) {
  try {
    const parsed = JSON.parse(window.localStorage.getItem(storageKey) || 'null');
    if (!parsed || parsed.version !== DASHBOARD_TOUR_VERSION) return null;
    return {
      version: DASHBOARD_TOUR_VERSION,
      status: String(parsed.status || 'not_started'),
      mode: normalizeTourMode(parsed.mode),
      sectionId: typeof parsed.sectionId === 'string' && parsed.sectionId ? parsed.sectionId : null,
      step: Math.max(0, Number.parseInt(parsed.step, 10) || 0),
      element: typeof parsed.element === 'string' && parsed.element ? parsed.element : null,
      completedIds: normalizeIdList(parsed.completedIds),
      promptDismissedIds: normalizeIdList(parsed.promptDismissedIds),
      updatedAt: parsed.updatedAt || null,
    };
  } catch (_) {
    return null;
  }
}

let dashboardTourProgressSync = null;

export function setDashboardTourProgressSync(sync) {
  dashboardTourProgressSync = typeof sync === 'function' ? sync : null;
}

export function chooseTourProgress(local, remote) {
  if (!remote) return local || null;
  if (!local) return remote;
  const remoteTime = Date.parse(remote.updatedAt || '') || 0;
  const localTime = Date.parse(local.updatedAt || '') || 0;
  const newer = remoteTime >= localTime ? remote : local;
  const older = newer === remote ? local : remote;
  return {
    ...newer,
    completedIds: uniqIds([...(older.completedIds || []), ...(newer.completedIds || [])]),
    promptDismissedIds: uniqIds([
      ...(older.promptDismissedIds || []),
      ...(newer.promptDismissedIds || []),
    ]),
  };
}

function normalizeRemoteTourProgress(progress) {
  if (!progress || Number(progress.version) !== DASHBOARD_TOUR_VERSION) return null;
  const status = String(progress.status || '');
  if (
    status !== 'not_started' &&
    status !== 'in_progress' &&
    status !== 'extended_in_progress' &&
    status !== 'section_in_progress' &&
    status !== 'completed' &&
    status !== 'dismissed'
  ) {
    return null;
  }
  return {
    version: DASHBOARD_TOUR_VERSION,
    status,
    mode: normalizeTourMode(progress.mode),
    sectionId: typeof progress.sectionId === 'string' && progress.sectionId ? progress.sectionId : null,
    step: Math.max(0, Number.parseInt(progress.step, 10) || 0),
    element: typeof progress.element === 'string' && progress.element ? progress.element : null,
    completedIds: normalizeIdList(progress.completedIds),
    promptDismissedIds: normalizeIdList(progress.promptDismissedIds),
    updatedAt: progress.updatedAt || null,
  };
}

export function writeDashboardTourProgress(
  storageKey,
  status,
  step = 0,
  mode = TOUR_MODE_OVERVIEW,
  element = null,
  options = {}
) {
  let previous = null;
  try {
    previous = JSON.parse(window.localStorage.getItem(storageKey) || 'null');
  } catch (_) {
    previous = null;
  }
  const previousCompleted =
    previous?.version === DASHBOARD_TOUR_VERSION ? normalizeIdList(previous.completedIds) : [];
  const previousDismissed =
    previous?.version === DASHBOARD_TOUR_VERSION
      ? normalizeIdList(previous.promptDismissedIds)
      : [];

  const progress = {
    version: DASHBOARD_TOUR_VERSION,
    status,
    mode: normalizeTourMode(mode),
    sectionId:
      typeof options.sectionId === 'string' && options.sectionId
        ? options.sectionId
        : null,
    step: Math.max(0, Number.parseInt(step, 10) || 0),
    element: typeof element === 'string' && element ? element : null,
    completedIds: Array.isArray(options.completedIds)
      ? normalizeIdList(options.completedIds)
      : previousCompleted,
    promptDismissedIds: Array.isArray(options.promptDismissedIds)
      ? normalizeIdList(options.promptDismissedIds)
      : previousDismissed,
    updatedAt: options.updatedAt || new Date().toISOString(),
  };
  try {
    window.localStorage.setItem(storageKey, JSON.stringify(progress));
  } catch (_) {
    // The tour still works when storage is unavailable (private mode or policy).
  }
  if (options.sync !== false && dashboardTourProgressSync) {
    dashboardTourProgressSync(storageKey, progress);
  }
  return progress;
}

export function markTourOptionCompleted(storageKey, optionId) {
  if (!optionId) return readDashboardTourProgress(storageKey);
  const current = readDashboardTourProgress(storageKey) || {
    status: 'completed',
    mode: TOUR_MODE_OVERVIEW,
    step: 0,
    element: null,
    sectionId: null,
    completedIds: [],
    promptDismissedIds: [],
  };
  const completedIds = uniqIds([...(current.completedIds || []), optionId]);
  return writeDashboardTourProgress(
    storageKey,
    current.status || 'completed',
    current.step || 0,
    current.mode || TOUR_MODE_OVERVIEW,
    current.element || null,
    {
      sectionId: current.sectionId,
      completedIds,
      promptDismissedIds: current.promptDismissedIds || [],
    }
  );
}

export function dismissTourPagePrompt(storageKey, optionId) {
  if (!optionId) return readDashboardTourProgress(storageKey);
  const current = readDashboardTourProgress(storageKey) || {
    status: 'not_started',
    mode: TOUR_MODE_OVERVIEW,
    step: 0,
    element: null,
    sectionId: null,
    completedIds: [],
    promptDismissedIds: [],
  };
  const promptDismissedIds = uniqIds([...(current.promptDismissedIds || []), optionId]);
  return writeDashboardTourProgress(
    storageKey,
    current.status || 'not_started',
    current.step || 0,
    current.mode || TOUR_MODE_OVERVIEW,
    current.element || null,
    {
      sectionId: current.sectionId,
      completedIds: current.completedIds || [],
      promptDismissedIds,
    }
  );
}

export function shouldAutoStartDashboardTour(progress) {
  // TEMP: do not auto-start brand-new users (not_started / missing progress).
  // Still resume an in-progress tour after refresh or a menu-driven page change.
  if (!progress) return false;
  return (
    progress.status === 'in_progress' ||
    progress.status === 'extended_in_progress' ||
    progress.status === 'section_in_progress'
  );
}

export function buildDashboardTourSteps({ isNarrowViewport = false } = {}) {
  const steps = [
    {
      page: 'dashboard',
      popover: {
        title: 'Welcome to xSPECTRE',
        description:
          'This quick tour shows you where to check your security posture, find risks, scope data, and get help.',
        side: 'bottom',
        align: 'center',
      },
    },
  ];

  if (!isNarrowViewport) {
    steps.push({
      page: 'dashboard',
      element: '[data-tour="sidebar-navigation"]',
      popover: {
        title: 'Navigate xSPECTRE',
        description:
          'Use this menu to manage assets and scans, investigate vulnerabilities, build reports, and access intelligence and settings.',
        side: 'right',
        align: 'start',
      },
    });
  }

  steps.push(
    {
      page: 'dashboard',
      element: '[data-tour="universal-search"]',
      popover: {
        title: 'Search across your workspace',
        description:
          'Search assets, scans, connectors, and vulnerabilities without leaving the page you are working on.',
        side: 'bottom',
        align: 'start',
      },
    },
    {
      page: 'dashboard',
      element: '[data-tour="scope-controls"]',
      popover: {
        title: 'Choose your scope',
        description:
          'Filter dashboard information by organization or site. Theme, notifications, profile, and this tour are available here too.',
        side: 'bottom',
        align: 'end',
      },
    },
    {
      page: 'dashboard',
      element: '[data-tour="header-scan-in-progress"]',
      popover: {
        title: 'Scans in progress',
        description:
          'The left count is scans that are running right now. Click it to open Web Scans filtered to in-progress jobs.',
        side: 'bottom',
        align: 'end',
      },
    },
    {
      page: 'dashboard',
      element: '[data-tour="header-scan-scheduled"]',
      popover: {
        title: 'Scheduled scans',
        description:
          'The right count is scans waiting to run. Click it to open scheduled jobs, change timing, or confirm nothing is queued before you create a new scan.',
        side: 'bottom',
        align: 'end',
      },
    },
    {
      page: 'dashboard',
      element: '[data-tour="scans-new-scan"]',
      popover: {
        title: 'New Scan',
        description:
          'Start a scan from the dashboard or the Scans page. On the dashboard this opens a type picker so you can choose web or network before you fill in targets and schedule.',
        side: 'bottom',
        align: 'end',
      },
    },
    {
      page: 'dashboard',
      element: '[data-tour="new-scan-modal"]',
      popover: {
        title: 'Choose a scan type',
        description:
          'Every new scan starts here. Web scans cover applications and URLs. Network scans cover on-prem and perimeter hosts. Pick one to open that scan manager.',
        side: 'left',
        align: 'start',
      },
      tourAction: TOUR_ACTION_OPEN_NEW_SCAN,
      requiresNewScanModal: true,
      forceDrive: true,
    },
    {
      page: 'dashboard',
      element: '[data-tour="header-profile"]',
      popover: {
        title: 'Your profile',
        description:
          'Open your avatar menu for Profile settings, Guided tours, and sign out.',
        side: 'bottom',
        align: 'end',
      },
    },
    {
      page: 'dashboard',
      element: '[data-tour="profile-menu"]',
      popover: {
        title: 'Profile menu',
        description:
          'Your name, email, organization, and role appear at the top. Use the items below to change account settings or leave the session.',
        side: 'left',
        align: 'start',
      },
      tourAction: TOUR_ACTION_OPEN_PROFILE_MENU,
      requiresProfileMenu: true,
      forceDrive: true,
    },
    {
      page: 'dashboard',
      element: '[data-tour="profile-settings-item"]',
      popover: {
        title: 'Profile settings',
        description:
          'Open this item to edit your name, contact details, password, and multi-factor authentication.',
        side: 'left',
        align: 'start',
      },
      tourAction: TOUR_ACTION_OPEN_PROFILE_MENU,
      requiresProfileMenu: true,
    },
    {
      page: 'profileSettings',
      element: '[data-tour="profile-settings-page"]',
      popover: {
        title: 'Profile settings',
        description:
          'General holds identity and password. Security & Access holds MFA and trusted devices.',
        side: 'bottom',
        align: 'start',
      },
      forceDrive: true,
    },
    {
      page: 'profileSettings',
      element: '[data-tour="profile-settings-tabs"]',
      popover: {
        title: 'Profile sections',
        description: 'Switch between General and Security & Access without leaving this page.',
        side: 'bottom',
        align: 'start',
      },
      tourAction: `${TOUR_ACTION_PROFILE_TAB_PREFIX}personal_info`,
    },
    {
      page: 'profileSettings',
      element: '[data-tour="profile-settings-account"], [data-tour="profile-settings-name"]',
      popover: {
        title: 'Legal name',
        description: 'Update the first and last name shown on your account and in the profile menu.',
        side: 'left',
        align: 'start',
      },
      tourAction: `${TOUR_ACTION_PROFILE_TAB_PREFIX}personal_info`,
    },
    {
      page: 'profileSettings',
      element: '[data-tour="profile-settings-password"]',
      popover: {
        title: 'Password',
        description: 'Change your current password here. Save Changes in the header writes the update.',
        side: 'left',
        align: 'start',
      },
      tourAction: `${TOUR_ACTION_PROFILE_TAB_PREFIX}personal_info`,
    },
    {
      page: 'profileSettings',
      element: '[data-tour="profile-settings-tab-security"]',
      popover: {
        title: 'Security and access',
        description: 'Open this tab to manage MFA methods and how you sign in.',
        side: 'bottom',
        align: 'start',
      },
      tourAction: `${TOUR_ACTION_PROFILE_TAB_PREFIX}account_security`,
      forceDrive: true,
    },
    {
      page: 'profileSettings',
      element: '[data-tour="profile-settings-mfa"]',
      popover: {
        title: 'Multi-factor authentication',
        description:
          'Choose email, SMS, authenticator app, or recovery codes. Only one primary method is active at a time.',
        side: 'left',
        align: 'start',
      },
      tourAction: `${TOUR_ACTION_PROFILE_TAB_PREFIX}account_security`,
    },
    {
      page: 'dashboard',
      element: '[data-tour="security-overview"]',
      popover: {
        title: 'Understand your security posture',
        description:
          'Your overall security score and severity totals provide a fast view of the areas that need attention.',
        side: 'bottom',
        align: 'center',
      },
    },
    {
      page: 'dashboard',
      element: '[data-tour="vulnerability-overview"]',
      popover: {
        title: 'Prioritize exposed systems',
        description:
          'These tables identify the most vulnerable hosts and the open vulnerabilities that contribute the most risk.',
        side: 'top',
        align: 'center',
      },
    },
    {
      page: 'dashboard',
      element: '[data-tour="incident-trend"]',
      popover: {
        title: 'Track risk over time',
        description:
          'Use the incident trend to see whether critical, high, medium, and low findings are improving or increasing.',
        side: 'top',
        align: 'center',
      },
    },
    {
      page: 'dashboard',
      element: '[data-tour="tour-help"]',
      popover: {
        title: 'Explore more tours',
        description:
          'Open Guided tours in your profile menu anytime for Assets, Scans, Vulnerabilities, and more. Completed sections are checked off in the menu. Choose Next section to start with Assets.',
        side: 'left',
        align: 'start',
      },
      tourStepId: 'overview-help',
      nextSectionId: 'assets',
      tourAction: TOUR_ACTION_OPEN_PROFILE_MENU,
      requiresProfileMenu: true,
      disableActiveInteraction: false,
    }
  );

  return steps;
}

/** Reports list, creation wizard, and detail tabs (used in extended tour and Reports-only start). */
export function buildReportsTourSteps({ navTourAction = null } = {}) {
  const navStep = {
    page: 'reports',
    element: '[data-tour="nav-reports"]',
    popover: {
      title: 'Reports',
      description:
        'Build, filter, and share security reports for stakeholders without leaving the platform.',
      side: 'right',
      align: 'start',
    },
  };
  if (navTourAction) navStep.tourAction = navTourAction;

  return [
    navStep,
    {
      page: 'reports',
      element: '[data-tour="reports-type-filter"]',
      popover: {
        title: 'Report type',
        description: 'Filter by assessment or report template type.',
        side: 'bottom',
        align: 'start',
      },
    },
    {
      page: 'reports',
      element: '[data-tour="reports-status-filter"]',
      popover: {
        title: 'Status',
        description: 'Show only reports in a given lifecycle state (for example completed or in progress).',
        side: 'bottom',
        align: 'start',
      },
    },
    {
      page: 'reports',
      element: '[data-tour="reports-date-filter"]',
      popover: {
        title: 'Created date',
        description: 'Limit the table to reports created within a date range.',
        side: 'bottom',
        align: 'start',
      },
    },
    {
      page: 'reports',
      element: '[data-tour="reports-risk-slider"]',
      popover: {
        title: 'Risk score range',
        description: 'Slide the min and max risk scores to focus on reports in a severity band.',
        side: 'bottom',
        align: 'start',
      },
    },
    {
      page: 'reports',
      element: '[data-tour="reports-terminal-toggle"]',
      popover: {
        title: 'Failed & superseded',
        description: 'Include terminal statuses when you need full history, not just active reports.',
        side: 'bottom',
        align: 'start',
      },
    },
    {
      page: 'reports',
      element: '[data-tour="reports-table"]',
      popover: {
        title: 'Reports library',
        description:
          'Every generated report appears here with type, status, risk score, version count, and owner. Open a row for detail.',
        side: 'top',
        align: 'start',
      },
    },
    {
      page: 'reports',
      element: '[data-tour="reports-new-report"]',
      popover: {
        title: 'Open the wizard',
        description:
          'Select + New Report here. That click opens the Create Security Report window over the library.',
        side: 'bottom',
        align: 'end',
      },
      tourAction: TOUR_ACTION_OPEN_REPORT_WIZARD,
    },
    {
      page: 'reports',
      element: '[data-tour="reports-wizard-modal"]',
      popover: {
        title: 'Report wizard',
        description:
          'This window opened from + New Report. It walks through objective, scope, discovery, intelligence, and preview.',
        side: 'left',
        align: 'start',
      },
      requiresReportWizard: true,
    },
    {
      page: 'reports',
      element: '[data-tour="reports-wizard-stepper"]',
      popover: {
        title: 'Wizard steps',
        description: 'Track progress across objective, scope, discovery, intelligence, and preview.',
        side: 'bottom',
        align: 'start',
      },
      requiresReportWizard: true,
    },
    {
      page: 'reports',
      element: '[data-tour="reports-wizard-objective"]',
      popover: {
        title: 'Report objective',
        description: 'Choose the assessment type and audience; this drives narrative tone and section layout.',
        side: 'left',
        align: 'start',
      },
      requiresReportWizard: true,
    },
    {
      page: 'reports',
      element: '[data-tour="reports-row"]',
      popover: {
        title: 'Open a report',
        description:
          'Close the wizard, then click a library row. That is how you reach report detail — the tour opens the first row next.',
        side: 'top',
        align: 'start',
      },
      requiresReportsRow: true,
    },
    {
      page: 'reportDetail',
      element: '[data-tour="report-detail-tabs"]',
      popover: {
        title: 'Report detail',
        description:
          'This page opened from the row you selected. Use these tabs for overview, versions, compare, evidence, and exports.',
        side: 'bottom',
        align: 'start',
      },
      tourAction: TOUR_ACTION_OPEN_FIRST_REPORT,
      requiresReportsRow: true,
      requiresOpenReportDetail: true,
    },
    {
      page: 'reportDetail',
      element: '[data-tour="report-detail-overview"]',
      popover: {
        title: 'Overview',
        description: 'Title, type, status, risk score, scope, and generation timestamps for this report.',
        side: 'left',
        align: 'start',
      },
      requiresReportsRow: true,
      requiresOpenReportDetail: true,
      tourAction: `${TOUR_ACTION_REPORT_TAB_PREFIX}overview`,
    },
    {
      page: 'reportDetail',
      element: '[data-tour="report-detail-findings-summary"]',
      popover: {
        title: 'Findings summary',
        description: 'Severity breakdown captured when the report was generated.',
        side: 'left',
        align: 'start',
      },
      requiresReportsRow: true,
      requiresOpenReportDetail: true,
      tourAction: `${TOUR_ACTION_REPORT_TAB_PREFIX}overview`,
    },
    {
      page: 'reportDetail',
      element: '[data-tour="report-detail-overview-evidence"]',
      popover: {
        title: 'Overview evidence',
        description:
          'Source integrations and how many evidence records were captured when this report was generated.',
        side: 'left',
        align: 'start',
      },
      requiresReportsRow: true,
      requiresOpenReportDetail: true,
      tourAction: `${TOUR_ACTION_REPORT_TAB_PREFIX}overview`,
    },
    {
      page: 'reportDetail',
      element: '[data-tour="report-detail-generation-summary"]',
      popover: {
        title: 'Generation summary',
        description:
          'This snapshot is frozen at generation time. Use Versions and Evidence for deltas and full provenance.',
        side: 'left',
        align: 'start',
      },
      requiresReportsRow: true,
      requiresOpenReportDetail: true,
      tourAction: `${TOUR_ACTION_REPORT_TAB_PREFIX}overview`,
    },
    {
      page: 'reportDetail',
      element: '[data-tour="report-detail-tab-versions"]',
      popover: {
        title: 'Versions tab',
        description: 'Each regeneration creates a version with its own risk score and template metadata.',
        side: 'bottom',
        align: 'start',
      },
      requiresReportsRow: true,
      requiresOpenReportDetail: true,
      tourAction: `${TOUR_ACTION_REPORT_TAB_PREFIX}versions`,
    },
    {
      page: 'reportDetail',
      element: '[data-tour="report-detail-versions"]',
      popover: {
        title: 'Version timeline',
        description: 'Review version history and risk score deltas over time.',
        side: 'left',
        align: 'start',
      },
      requiresReportsRow: true,
      requiresOpenReportDetail: true,
      tourAction: `${TOUR_ACTION_REPORT_TAB_PREFIX}versions`,
    },
    {
      page: 'reportDetail',
      element: '[data-tour="report-detail-version-metadata"]',
      popover: {
        title: 'Version metadata',
        description:
          'Each version keeps its own template and generation metadata. Open Evidence to inspect the frozen snapshot for a version.',
        side: 'left',
        align: 'start',
      },
      requiresReportsRow: true,
      requiresOpenReportDetail: true,
      tourAction: `${TOUR_ACTION_REPORT_TAB_PREFIX}versions`,
    },
    {
      page: 'reportDetail',
      element: '[data-tour="report-detail-tab-compare"]',
      popover: {
        title: 'Compare tab',
        description: 'Diff two versions to see risk and finding changes between generations.',
        side: 'bottom',
        align: 'start',
      },
      requiresReportsRow: true,
      requiresOpenReportDetail: true,
      tourAction: `${TOUR_ACTION_REPORT_TAB_PREFIX}compare`,
    },
    {
      page: 'reportDetail',
      element: '[data-tour="report-detail-compare"]',
      popover: {
        title: 'Version comparison',
        description: 'Pick version A and B, then run compare to view added, removed, and severity shifts.',
        side: 'left',
        align: 'start',
      },
      requiresReportsRow: true,
      requiresOpenReportDetail: true,
      tourAction: `${TOUR_ACTION_REPORT_TAB_PREFIX}compare`,
    },
    {
      page: 'reportDetail',
      element: '[data-tour="report-detail-tab-evidence"]',
      popover: {
        title: 'Evidence tab',
        description: 'Immutable snapshots captured at generation time — live scanners are not re-queried.',
        side: 'bottom',
        align: 'start',
      },
      requiresReportsRow: true,
      requiresOpenReportDetail: true,
      tourAction: `${TOUR_ACTION_REPORT_TAB_PREFIX}evidence`,
    },
    {
      page: 'reportDetail',
      element: '[data-tour="report-detail-evidence"]',
      popover: {
        title: 'Evidence provenance',
        description: 'Inspect snapshot hash, sources, and searchable evidence items for the selected version.',
        side: 'left',
        align: 'start',
      },
      requiresReportsRow: true,
      requiresOpenReportDetail: true,
      tourAction: `${TOUR_ACTION_REPORT_TAB_PREFIX}evidence`,
    },
    {
      page: 'reportDetail',
      element: '[data-tour="report-detail-tab-export"]',
      popover: {
        title: 'Export tab',
        description: 'Generate PDF or evidence package exports and download completed artifacts.',
        side: 'bottom',
        align: 'start',
      },
      requiresReportsRow: true,
      requiresOpenReportDetail: true,
      tourAction: `${TOUR_ACTION_REPORT_TAB_PREFIX}export`,
    },
    {
      page: 'reportDetail',
      element: '[data-tour="report-detail-export"]',
      popover: {
        title: 'Export generation',
        description: 'Pick a version and format, generate exports, and track download-ready files in history.',
        side: 'left',
        align: 'start',
      },
      requiresReportsRow: true,
      requiresOpenReportDetail: true,
      tourAction: `${TOUR_ACTION_REPORT_TAB_PREFIX}export`,
    },
    {
      page: 'reportDetail',
      element: '[data-tour="report-detail-export-history"]',
      popover: {
        title: 'Export history',
        description:
          'Previous exports for this report: format, version, status, checksum, and download when the artifact is ready.',
        side: 'left',
        align: 'start',
      },
      requiresReportsRow: true,
      requiresOpenReportDetail: true,
      tourAction: `${TOUR_ACTION_REPORT_TAB_PREFIX}export`,
    },
    {
      page: 'reportDetail',
      element: '[data-tour="report-detail-back"]',
      popover: {
        title: 'Back to reports',
        description: 'Return to the full reports library when you are done reviewing this report.',
        side: 'bottom',
        align: 'start',
      },
      requiresReportsRow: true,
      requiresOpenReportDetail: true,
    },
  ];
}

export function buildSettingsTourSteps() {
  const detail = (page, element, title, description, extra = {}) => ({
    page,
    element,
    popover: { title, description, side: extra.side || 'left', align: extra.align || 'start' },
    ...extra,
  });

  return [
    {
      page: 'settingsConnectors',
      element: '[data-tour="nav-settings"]',
      popover: {
        title: 'Settings',
        description:
          'Open Settings to manage connectors, scanner deployment, users, organizations, and partners.',
        side: 'right',
        align: 'end',
      },
    },
    {
      page: 'settingsConnectors',
      element: '[data-tour="nav-settings-connectors"]',
      popover: {
        title: 'Connectors',
        description: 'Scanner assignments for web and network tools live under Connectors.',
        side: 'right',
        align: 'start',
      },
    },
    detail(
      'settingsConnectors',
      '[data-tour="settings-connectors-tabs"]',
      'Scanner type',
      'Switch between web and network scanners. Each tab lists connectors assigned to your sites.'
    ),
    detail(
      'settingsConnectors',
      '[data-tour="settings-connectors-search"]',
      'Search connectors',
      'Filter the list by scanner name or assignment details.',
      { side: 'bottom' }
    ),
    detail(
      'settingsConnectors',
      '[data-tour="settings-connectors-table"]',
      'Connector list',
      'Review assigned scanners, status, and site mapping. Row actions edit or remove a connector when your role allows it.',
      { side: 'top' }
    ),
    detail(
      'settingsConnectors',
      '[data-tour="settings-connectors-configure"]',
      'Configure connector',
      'The cog under Actions opens the connector settings popup for that scanner.',
      { side: 'left', requiresConnectorConfigure: true }
    ),
    detail(
      'settingsConnectors',
      '[data-tour="settings-connectors-tab-network"]',
      'Network scanners',
      'Open the Network Scanner tab for on-prem and perimeter network tools. The same cog opens that scanner’s settings popup.',
      { side: 'bottom', tourAction: `${TOUR_ACTION_CONNECTORS_TAB_PREFIX}network`, forceDrive: true }
    ),
    detail(
      'settingsConnectors',
      '[data-tour="settings-connectors-search"]',
      'Search network scanners',
      'Filter on-prem and perimeter connectors by name or assignment details.',
      { side: 'bottom', tourAction: `${TOUR_ACTION_CONNECTORS_TAB_PREFIX}network`, forceDrive: true }
    ),
    detail(
      'settingsConnectors',
      '[data-tour="settings-connectors-table"]',
      'Network connector list',
      'Each row is a network scanner assigned to your sites, with status and last update.',
      { side: 'top', tourAction: `${TOUR_ACTION_CONNECTORS_TAB_PREFIX}network`, forceDrive: true }
    ),
    detail(
      'settingsConnectors',
      '[data-tour="settings-connectors-configure"]',
      'Configure network scanner',
      'The cog opens OpenVAS settings for host, credentials, perimeter mode, and site assignment.',
      { side: 'left', tourAction: `${TOUR_ACTION_CONNECTORS_TAB_PREFIX}network`, requiresConnectorConfigure: true }
    ),
    {
      page: 'settingsDeployment',
      element: '[data-tour="nav-settings-deployment"]',
      popover: {
        title: 'Deployment',
        description: 'Create install tokens and review scanner install history from Deployment.',
        side: 'right',
        align: 'start',
      },
    },
    detail(
      'settingsDeployment',
      '[data-tour="settings-deployment-tabs"]',
      'Deployment sections',
      'General platform options, scanner tokens, and install attempts are split across these tabs.',
      { side: 'bottom' }
    ),
    detail(
      'settingsDeployment',
      '[data-tour="settings-deployment-general"]',
      'General',
      'Partner management, e-commerce, and outbound email controls for this platform.',
      { tourAction: `${TOUR_ACTION_DEPLOYMENT_TAB_PREFIX}general`, forceDrive: true }
    ),
    detail(
      'settingsDeployment',
      '[data-tour="settings-deployment-tab-tokens"]',
      'Scanner tokens',
      'Open this tab to create and manage one-time or reusable scanner install tokens.',
      { side: 'bottom', tourAction: `${TOUR_ACTION_DEPLOYMENT_TAB_PREFIX}tokens` }
    ),
    detail(
      'settingsDeployment',
      '[data-tour="settings-deployment-tokens"]',
      'Token inventory',
      'Create a token for a site and scanner type, then copy the install command. Revoke tokens that should no longer register.',
      { tourAction: `${TOUR_ACTION_DEPLOYMENT_TAB_PREFIX}tokens`, forceDrive: true }
    ),
    detail(
      'settingsDeployment',
      '[data-tour="settings-deployment-tab-attempts"]',
      'Install attempts',
      'Open this tab to see whether installs succeeded and diagnose failed registrations.',
      { side: 'bottom', tourAction: `${TOUR_ACTION_DEPLOYMENT_TAB_PREFIX}attempts` }
    ),
    detail(
      'settingsDeployment',
      '[data-tour="settings-deployment-attempts"]',
      'Install history',
      'Each attempt shows install ID, status, host fingerprint, and last check-in. Click an install ID to open its details.',
      { tourAction: `${TOUR_ACTION_DEPLOYMENT_TAB_PREFIX}attempts`, forceDrive: true }
    ),
    detail(
      'settingsDeployment',
      '[data-tour="settings-deployment-attempt-detail"]',
      'Attempt details',
      'Selecting an install ID fills this panel with host healthcheck data, installer version, errors, and token status.',
      {
        tourAction: TOUR_ACTION_SELECT_FIRST_ATTEMPT,
        requiresAttemptDetail: true,
        forceDrive: true,
      }
    ),
    {
      page: 'settingsManageUsers',
      element: '[data-tour="nav-settings-users"]',
      popover: {
        title: 'Users',
        description: 'Invite people, assign roles, and filter the directory by organization.',
        side: 'right',
        align: 'start',
      },
    },
    detail(
      'settingsManageUsers',
      '[data-tour="settings-users-add"]',
      'Add or invite',
      'Create a local account or send an email invite. Role and organization are set in the dialog.',
      { side: 'bottom' }
    ),
    detail(
      'settingsManageUsers',
      '[data-tour="settings-users-org-filter"]',
      'Organization filter',
      'Limit the user list to one organization, or show everyone you can manage.',
      { side: 'bottom' }
    ),
    detail(
      'settingsManageUsers',
      '[data-tour="settings-users-table"]',
      'User directory',
      'Name, email, role, organization, and last activity. Open a row to edit or disable a user.',
      { side: 'top' }
    ),
    {
      page: 'settingsUsersOrgs',
      element: '[data-tour="nav-settings-orgs"]',
      popover: {
        title: 'Organizations',
        description: 'Create tenants, assign sites, and control which scan products each org can use.',
        side: 'right',
        align: 'start',
      },
    },
    detail(
      'settingsUsersOrgs',
      '[data-tour="settings-orgs-add"]',
      'Add organization',
      'Create a new organization with name, description, and product flags (web, perimeter, e-commerce).',
      { side: 'bottom' }
    ),
    detail(
      'settingsUsersOrgs',
      '[data-tour="settings-orgs-search"]',
      'Search organizations',
      'Find an organization by name. Use the site filter when you need orgs tied to a specific site.',
      { side: 'bottom' }
    ),
    detail(
      'settingsUsersOrgs',
      '[data-tour="settings-orgs-table"]',
      'Organization list',
      'Open a row for platform flags, assigned sites, and user counts. Bulk delete is available when your role allows it.',
      { side: 'top' }
    ),
    {
      page: 'settingsPartners',
      element: '[data-tour="nav-settings-partners"]',
      popover: {
        title: 'Partners',
        description: 'Manage referral partners, commission tiers, and assigned client organizations.',
        side: 'right',
        align: 'start',
      },
    },
    detail(
      'settingsPartners',
      '[data-tour="settings-partners-add"]',
      'Add partner',
      'Create a partner record with email, tier, commission rate, and assigned organizations.',
      { side: 'bottom' }
    ),
    detail(
      'settingsPartners',
      '[data-tour="settings-partners-table"]',
      'Partner list',
      'Referral code, tier, rate, and assigned orgs. Edit or delete from the row actions.',
      { side: 'top' }
    ),
  ];
}

export function buildSubscriptionTourSteps({ navTourAction } = {}) {
  const detail = (page, element, title, description, extra = {}) => ({
    page,
    element,
    popover: { title, description, side: extra.side || 'left', align: extra.align || 'start' },
    ...extra,
  });

  const navStep = {
    page: 'subscription',
    element: '[data-tour="nav-subscription"]',
    popover: {
      title: 'Subscription',
      description:
        'Open Subscription to review organization plans, usage, billing contacts, invoices, and add-ons.',
      side: 'right',
      align: 'start',
    },
  };
  if (navTourAction) navStep.tourAction = navTourAction;

  return [
    navStep,
    detail(
      'subscription',
      '[data-tour="subscription-org-table"]',
      'Billing organizations',
      'Each row is an organization you can bill. Status shows Trial, Active, or Manual.',
      { side: 'top' }
    ),
    detail(
      'subscription',
      '[data-tour="subscription-view-plans"]',
      'View plans and billing',
      'Open an organization to see plan and usage, then billing details, payment method, invoices, and add-ons.',
      { side: 'left' }
    ),
    detail(
      'subscriptionOrgDetail',
      '[data-tour="subscription-detail-tabs"]',
      'Billing sections',
      'Plan and usage is the default. Switch tabs for billing contact, payment method, invoices, and the plan catalog.',
      {
        side: 'bottom',
        tourAction: TOUR_ACTION_OPEN_FIRST_SUBSCRIPTION_ORG,
        requiresOpenSubscriptionDetail: true,
        forceDrive: true,
      }
    ),
    detail(
      'subscriptionOrgDetail',
      '[data-tour="subscription-plan-overview"]',
      'Organisation and plan',
      'Current plan, subscription status, trial dates, and the latest billed amount for this organization.',
      {
        tourAction: `${TOUR_ACTION_SUBSCRIPTION_TAB_PREFIX}usage`,
        requiresOpenSubscriptionDetail: true,
        forceDrive: true,
      }
    ),
    detail(
      'subscriptionOrgDetail',
      '[data-tour="subscription-usage-limits"]',
      'Usage vs included limits',
      'Compare used capacity to what the plan includes, plus overage and add-on impact.',
      {
        side: 'top',
        tourAction: `${TOUR_ACTION_SUBSCRIPTION_TAB_PREFIX}usage`,
        requiresOpenSubscriptionDetail: true,
        forceDrive: true,
      }
    ),
    detail(
      'subscriptionOrgDetail',
      '[data-tour="subscription-usage-meters"]',
      'Usage this period',
      'Meters for perimeter IPs, on-prem IPs, web app FQDNs, and early disclosure when licensed.',
      {
        side: 'top',
        tourAction: `${TOUR_ACTION_SUBSCRIPTION_TAB_PREFIX}usage`,
        requiresOpenSubscriptionDetail: true,
        forceDrive: true,
      }
    ),
    detail(
      'subscriptionOrgDetail',
      '[data-tour="subscription-site-usage"]',
      'Site usage',
      'Per-site consumption of perimeter, on-prem, and web capacity. Search to find a site quickly.',
      {
        side: 'top',
        tourAction: `${TOUR_ACTION_SUBSCRIPTION_TAB_PREFIX}usage`,
        requiresOpenSubscriptionDetail: true,
        forceDrive: true,
      }
    ),
    detail(
      'subscriptionOrgDetail',
      '[data-tour="subscription-tab-billing"]',
      'Billing details',
      'Open billing contact and address for the linked billing account.',
      {
        side: 'bottom',
        tourAction: `${TOUR_ACTION_SUBSCRIPTION_TAB_PREFIX}billing`,
        requiresOpenSubscriptionDetail: true,
        forceDrive: true,
      }
    ),
    detail(
      'subscriptionOrgDetail',
      '[data-tour="subscription-billing-panel"]',
      'Billing contact',
      'Edit the billing name, email, and address used on invoices when your role allows it.',
      {
        tourAction: `${TOUR_ACTION_SUBSCRIPTION_TAB_PREFIX}billing`,
        requiresOpenSubscriptionDetail: true,
        forceDrive: true,
      }
    ),
    detail(
      'subscriptionOrgDetail',
      '[data-tour="subscription-tab-payment"]',
      'Payment method',
      'Open the secure payment form. Card details are handled by the payment provider, not stored in xSPECTRE.',
      {
        side: 'bottom',
        tourAction: `${TOUR_ACTION_SUBSCRIPTION_TAB_PREFIX}payment`,
        requiresOpenSubscriptionDetail: true,
        forceDrive: true,
      }
    ),
    detail(
      'subscriptionOrgDetail',
      '[data-tour="subscription-payment-panel"]',
      'Payment form',
      'Add or replace the card on file for this organization when billing is configured.',
      {
        tourAction: `${TOUR_ACTION_SUBSCRIPTION_TAB_PREFIX}payment`,
        requiresOpenSubscriptionDetail: true,
        forceDrive: true,
      }
    ),
    detail(
      'subscriptionOrgDetail',
      '[data-tour="subscription-tab-invoices"]',
      'Invoices',
      'Open invoice history for this organization.',
      {
        side: 'bottom',
        tourAction: `${TOUR_ACTION_SUBSCRIPTION_TAB_PREFIX}invoices`,
        requiresOpenSubscriptionDetail: true,
        forceDrive: true,
      }
    ),
    detail(
      'subscriptionOrgDetail',
      '[data-tour="subscription-invoices-panel"]',
      'Invoice history',
      'Download or review past invoices, amounts, and payment status.',
      {
        tourAction: `${TOUR_ACTION_SUBSCRIPTION_TAB_PREFIX}invoices`,
        requiresOpenSubscriptionDetail: true,
        forceDrive: true,
      }
    ),
    detail(
      'subscriptionOrgDetail',
      '[data-tour="subscription-tab-plans"]',
      'Plans and add-ons',
      'Open the catalog to compare tiers and add capacity.',
      {
        side: 'bottom',
        tourAction: `${TOUR_ACTION_SUBSCRIPTION_TAB_PREFIX}plans`,
        requiresOpenSubscriptionDetail: true,
        forceDrive: true,
      }
    ),
    detail(
      'subscriptionOrgDetail',
      '[data-tour="subscription-plans-panel"]',
      'Plan catalog',
      'Choose a tier, review included limits, and add extras such as extra IPs or web apps.',
      {
        tourAction: `${TOUR_ACTION_SUBSCRIPTION_TAB_PREFIX}plans`,
        requiresOpenSubscriptionDetail: true,
        forceDrive: true,
      }
    ),
    detail(
      'subscriptionOrgDetail',
      '[data-tour="subscription-org-back"]',
      'Back to subscription',
      'Return to the organization list without leaving Subscription.',
      {
        side: 'bottom',
        tourAction: `${TOUR_ACTION_SUBSCRIPTION_TAB_PREFIX}plans`,
        requiresOpenSubscriptionDetail: true,
        forceDrive: true,
      }
    ),
  ];
}

export function buildSupportTourSteps({ navTourAction } = {}) {
  const detail = (page, element, title, description, extra = {}) => ({
    page,
    element,
    popover: { title, description, side: extra.side || 'left', align: extra.align || 'start' },
    ...extra,
  });

  const navStep = {
    page: 'support',
    element: '[data-tour="nav-support"]',
    popover: {
      title: 'Support',
      description:
        'Open Support to track existing tickets or send a new request to the xSPECTRE team.',
      side: 'right',
      align: 'end',
    },
  };
  if (navTourAction) navStep.tourAction = navTourAction;

  return [
    navStep,
    detail(
      'support',
      '[data-tour="support-tabs"]',
      'Support views',
      'My Tickets lists requests you already opened. New Request starts a ticket for the support team.',
      { side: 'bottom', tourAction: `${TOUR_ACTION_SUPPORT_TAB_PREFIX}tickets` }
    ),
    detail(
      'support',
      '[data-tour="support-search"]',
      'Search tickets',
      'Filter by ticket number, subject, status, or priority.',
      { side: 'bottom', tourAction: `${TOUR_ACTION_SUPPORT_TAB_PREFIX}tickets` }
    ),
    detail(
      'support',
      '[data-tour="support-ticket-list"]',
      'Ticket list',
      'Each row shows ticket ID, subject, status, priority, and created time. Click a row to open its conversation.',
      { side: 'top', tourAction: `${TOUR_ACTION_SUPPORT_TAB_PREFIX}tickets` }
    ),
    detail(
      'support',
      '[data-tour="support-ticket-detail"]',
      'Ticket details',
      'Selecting a ticket shows the subject, status, and description. Reply and activity sit in this same panel.',
      {
        side: 'left',
        tourAction: TOUR_ACTION_SELECT_FIRST_TICKET,
      }
    ),
    detail(
      'support',
      '[data-tour="support-ticket-reply"]',
      'Reply',
      'Add a follow-up for the support team. They are notified when you post.',
      {
        side: 'left',
        tourAction: TOUR_ACTION_SELECT_FIRST_TICKET,
      }
    ),
    detail(
      'support',
      '[data-tour="support-ticket-timeline"]',
      'Activity',
      'Replies and status changes appear here in order, so you can see what happened on the ticket.',
      {
        side: 'left',
        tourAction: TOUR_ACTION_SELECT_FIRST_TICKET,
      }
    ),
    detail(
      'support',
      '[data-tour="support-tab-create"]',
      'New request',
      'Open this tab to file a new support ticket.',
      { side: 'bottom', tourAction: `${TOUR_ACTION_SUPPORT_TAB_PREFIX}create` }
    ),
    detail(
      'support',
      '[data-tour="support-create-form"]',
      'Create support request',
      'Pick a category, describe the issue, and submit. The team follows up by email.',
      { side: 'left', tourAction: `${TOUR_ACTION_SUPPORT_TAB_PREFIX}create` }
    ),
    detail(
      'support',
      '[data-tour="support-create-category"]',
      'Category',
      'Choose the area that matches your request: account, dashboard, reports, scanners, features, or general.',
      { side: 'left', tourAction: `${TOUR_ACTION_SUPPORT_TAB_PREFIX}create` }
    ),
    detail(
      'support',
      '[data-tour="support-create-details"]',
      'Request details',
      'Subject, priority, and description. Include scan IDs or asset names when they help reproduce the issue.',
      { side: 'left', tourAction: `${TOUR_ACTION_SUPPORT_TAB_PREFIX}create` }
    ),
    detail(
      'support',
      '[data-tour="support-create-attachments"]',
      'Attachments',
      'Attach screenshots, logs, or exports. Choose files or drag them into the drop zone.',
      { side: 'top', tourAction: `${TOUR_ACTION_SUPPORT_TAB_PREFIX}create` }
    ),
    detail(
      'support',
      '[data-tour="support-create-submit"]',
      'Submit request',
      'Submit opens the ticket and emails you when status changes.',
      { side: 'top', tourAction: `${TOUR_ACTION_SUPPORT_TAB_PREFIX}create` }
    ),
  ];
}

export function buildExtendedTourSteps({ isNarrowViewport = false } = {}) {
  const steps = [
    {
      page: 'targets',
      element: '[data-tour="nav-targets"]',
      popover: {
        title: 'Assets',
        description:
          'Open Assets to inventory the hosts and applications in scope and keep your attack surface current.',
        side: 'right',
        align: 'start',
      },
    },
    {
      page: 'targets',
      element: '[data-tour="assets-search"]',
      popover: {
        title: 'Search assets',
        description:
          'Filter the inventory by hostname, IP, site, MAC, or other visible attributes without leaving the page.',
        side: 'bottom',
        align: 'end',
      },
    },
    {
      page: 'targets',
      element: '[data-tour="assets-table"]',
      popover: {
        title: 'Inventory table',
        description:
          'Browse site, IP, hostname, type, MAC, and certificate counts. Sort columns from the header, and click a row to open full asset detail.',
        side: 'top',
        align: 'center',
      },
    },
    {
      page: 'targets',
      element: '[data-tour="assets-select-all"]',
      popover: {
        title: 'Select assets',
        description:
          'Use the header checkbox to select every visible row, or tick individual rows. Selection unlocks bulk Exclude and Delete.',
        side: 'right',
        align: 'start',
      },
      requiresAssetsRow: true,
    },
    {
      page: 'targets',
      element: '[data-tour="assets-bulk-actions"]',
      popover: {
        title: 'Bulk actions',
        description:
          'With assets selected, Exclude/Include or Delete them here. Bulk actions apply only to inventory-backed assets.',
        side: 'bottom',
        align: 'start',
      },
      tourAction: TOUR_ACTION_SELECT_FIRST_ASSET,
      requiresAssetsRow: true,
      requiresAssetSelection: true,
    },
    {
      page: 'targets',
      element: '[data-tour="assets-row-actions"]',
      popover: {
        title: 'Row actions',
        description:
          'Exclude or include an asset from scanning, or delete it, without opening the detail view. Confirmations protect against accidental changes.',
        side: 'left',
        align: 'start',
      },
      tourAction: TOUR_ACTION_CLEAR_ASSET_SELECTION,
      requiresAssetsRow: true,
    },
    {
      page: 'targets',
      element: '[data-tour="assets-pagination"]',
      popover: {
        title: 'Pagination',
        description:
          'Large inventories are paged here so you can move through assets without loading every row at once.',
        side: 'top',
        align: 'end',
      },
    },
    {
      page: 'targets',
      // Highlight a visible cell, not the full wide <tr> (overflows small viewports).
      element: '[data-tour="assets-row-open"]',
      popover: {
        title: 'Open asset detail',
        description:
          'Click this highlighted row to open the asset’s full record, or press Next and the tour will open it for you.',
        side: 'top',
        align: 'center',
      },
      requiresAssetsRow: true,
      disableActiveInteraction: false,
      advanceOnClick: true,
    },
    {
      page: 'targets',
      element: '[data-tour="assets-detail-modal"]',
      popover: {
        title: 'Asset detail',
        description:
          'This is the full asset record: identity, software, ports, certificates, and related vulnerabilities.',
        side: 'left',
        align: 'start',
      },
      tourAction: TOUR_ACTION_OPEN_FIRST_ASSET,
      requiresAssetsRow: true,
      requiresOpenAssetDetail: true,
    },
    {
      page: 'targets',
      element: '[data-tour="assets-detail-actions"]',
      popover: {
        title: 'Exclude from here',
        description:
          'Exclude or include this asset from scanning directly from the detail header when you are reviewing a specific host.',
        side: 'left',
        align: 'start',
      },
      requiresAssetsRow: true,
      requiresOpenAssetDetail: true,
    },
    {
      page: 'targets',
      element: '[data-tour="assets-detail-summary"]',
      popover: {
        title: 'Asset summary',
        description:
          'Confirm IP, hostname, site, OS, MAC, manufacturer, vulnerability count, and last scan for the selected asset.',
        side: 'bottom',
        align: 'center',
      },
      requiresAssetsRow: true,
      requiresOpenAssetDetail: true,
    },
    {
      page: 'targets',
      element: '[data-tour="assets-detail-software-ports"]',
      popover: {
        title: 'Software and ports',
        description:
          'Review discovered software packages and open ports. Software entries can open deeper detail when available.',
        side: 'top',
        align: 'center',
      },
      requiresAssetsRow: true,
      requiresOpenAssetDetail: true,
    },
    {
      page: 'targets',
      element: '[data-tour="assets-detail-certificates"]',
      popover: {
        title: 'Certificates',
        description:
          'Inspect TLS certificates for this asset, including subject, SANs, issuer, validity window, and ports.',
        side: 'top',
        align: 'center',
      },
      requiresAssetsRow: true,
      requiresOpenAssetDetail: true,
    },
    {
      page: 'targets',
      element: '[data-tour="assets-detail-vulnerabilities"]',
      popover: {
        title: 'Asset vulnerabilities',
        description:
          'See findings tied to this asset and open a vulnerability for remediation context. Close the dialog when you are ready to continue.',
        side: 'top',
        align: 'center',
      },
      requiresAssetsRow: true,
      requiresOpenAssetDetail: true,
    },
    {
      page: 'scansWeb',
      element: '[data-tour="nav-scans"]',
      popover: {
        title: 'Scans',
        description:
          'Launch and track web and network scans from here. Results feed the risks and posture views on your dashboard.',
        side: 'right',
        align: 'start',
      },
      tourAction: TOUR_ACTION_CLOSE_ASSET_DETAIL,
    },
    {
      page: 'scansWeb',
      element: '[data-tour="nav-scans-web"]',
      popover: {
        title: 'Web Scans',
        description:
          'Open Web Scans to review application scan history, status, and findings for the current scope.',
        side: 'right',
        align: 'start',
      },
    },
    {
      page: 'scansWeb',
      element: '[data-tour="scans-new-scan"]',
      popover: {
        title: 'Start a new scan',
        description:
          'Use + New Scan to configure and launch a scan. On this page it opens the matching web or network scan manager.',
        side: 'bottom',
        align: 'end',
      },
    },
    {
      page: 'scansWeb',
      element: '[data-tour="scans-status-filter"]',
      popover: {
        title: 'Filter by status',
        description:
          'Narrow the list to all scans, completed, in progress, scheduled, failed, or recovering.',
        side: 'bottom',
        align: 'start',
      },
    },
    {
      page: 'scansWeb',
      element: '[data-tour="scans-search"]',
      popover: {
        title: 'Search scans',
        description:
          'Find scans by target, status, or other visible fields without leaving the Scans page.',
        side: 'bottom',
        align: 'end',
      },
    },
    {
      page: 'scansWeb',
      element: '[data-tour="scans-table"]',
      popover: {
        title: 'Scan history',
        description:
          'Browse targets, status, dates, and risk totals. Open a completed scan target to view full scan detail.',
        side: 'top',
        align: 'center',
      },
    },
    {
      page: 'scansWeb',
      element: '[data-tour="scans-select-all"]',
      popover: {
        title: 'Select scans',
        description:
          'Select visible scans to unlock bulk Delete, Rerun, or Stop actions in the toolbar.',
        side: 'right',
        align: 'start',
      },
      requiresScansRow: true,
    },
    {
      page: 'scansWeb',
      element: '[data-tour="scans-bulk-actions"]',
      popover: {
        title: 'Bulk scan actions',
        description:
          'With scans selected, delete them in bulk, rerun failed scans, or stop in-progress scans when available.',
        side: 'bottom',
        align: 'start',
      },
      tourAction: TOUR_ACTION_SELECT_FIRST_SCAN,
      requiresScansRow: true,
      requiresScanSelection: true,
    },
    {
      page: 'scansWeb',
      element: '[data-tour="scans-row-actions"]',
      popover: {
        title: 'Row actions',
        description:
          'Rerun, stop, or delete an individual scan from the row actions without using bulk selection.',
        side: 'left',
        align: 'start',
      },
      tourAction: TOUR_ACTION_CLEAR_SCAN_SELECTION,
      requiresScansRow: true,
    },
    {
      page: 'scansNetwork',
      element: '[data-tour="nav-scans-network"]',
      popover: {
        title: 'On-premise',
        description:
          'Open On-premise for internal network scan activity in the current organization and site scope.',
        side: 'right',
        align: 'start',
      },
      tourAction: TOUR_ACTION_NETWORK_SCOPE_ONPREM,
    },
    {
      page: 'scansNetwork',
      element: '[data-tour="scans-network-panel-onprem"]',
      popover: {
        title: 'On-premise scan list',
        description:
          'Search, filter by status, select rows, and use bulk or row actions for on-premise scans here.',
        side: 'top',
        align: 'center',
      },
      tourAction: TOUR_ACTION_NETWORK_SCOPE_ONPREM,
    },
    {
      page: 'scansPerimeter',
      element: '[data-tour="nav-scans-perimeter"]',
      popover: {
        title: 'Perimeter',
        description:
          'Open Perimeter for external-facing network scans and their risk totals.',
        side: 'right',
        align: 'start',
      },
      tourAction: TOUR_ACTION_NETWORK_SCOPE_PERIMETER,
    },
    {
      page: 'scansPerimeter',
      element: '[data-tour="scans-network-panel-perimeter"]',
      popover: {
        title: 'Perimeter scan list',
        description:
          'Perimeter uses the same table tools — status filter, search, selection, and row actions — for external scans.',
        side: 'top',
        align: 'center',
      },
      tourAction: TOUR_ACTION_NETWORK_SCOPE_PERIMETER,
    },
    {
      page: 'risks',
      element: '[data-tour="nav-risks"]',
      popover: {
        title: 'Vulnerabilities',
        description:
          'Investigate open findings, update status, and prioritize remediation across your organization and sites.',
        side: 'right',
        align: 'start',
      },
    },
    {
      page: 'risks',
      element: '[data-tour="risks-severity-filter"]',
      popover: {
        title: 'Threat filter',
        description:
          'Narrow findings by severity so you can focus on critical and high threats first.',
        side: 'bottom',
        align: 'start',
      },
    },
    {
      page: 'risks',
      element: '[data-tour="risks-scanner-filter"]',
      popover: {
        title: 'Scanner filter',
        description:
          'Show findings from a specific scanner type when you need to isolate web, network, or other sources.',
        side: 'bottom',
        align: 'start',
      },
    },
    {
      page: 'risks',
      element: '[data-tour="risks-status-filter"]',
      popover: {
        title: 'Status filter',
        description:
          'Filter by remediation status — open, in progress, resolved, and related workflow states.',
        side: 'bottom',
        align: 'start',
      },
    },
    {
      page: 'risks',
      element: '[data-tour="risks-search"]',
      popover: {
        title: 'Search vulnerabilities',
        description:
          'Search by title, target, CVE, or other visible attributes without leaving the list.',
        side: 'bottom',
        align: 'end',
      },
    },
    {
      page: 'risks',
      element: '[data-tour="risks-table"]',
      popover: {
        title: 'Findings table',
        description:
          'Review severity, target, port, CVSS, title, and scanner source. Sort columns from the header, and click a row to open full finding detail.',
        side: 'top',
        align: 'center',
      },
    },
    {
      page: 'risks',
      element: '[data-tour="risks-select-all"]',
      popover: {
        title: 'Select findings',
        description:
          'Use the header checkbox to select every visible row, or tick individual findings. Selection unlocks bulk status changes, delete, and report generation.',
        side: 'right',
        align: 'start',
      },
      requiresRisksRow: true,
    },
    {
      page: 'risks',
      element: '[data-tour="risks-bulk-actions"]',
      popover: {
        title: 'Bulk actions',
        description:
          'With findings selected, change status in bulk or delete them here. Confirmations protect against accidental changes.',
        side: 'bottom',
        align: 'start',
      },
      tourAction: TOUR_ACTION_SELECT_FIRST_RISK,
      requiresRisksRow: true,
      requiresRiskSelection: true,
    },
    {
      page: 'risks',
      element: '[data-tour="risks-generate-report"]',
      popover: {
        title: 'Generate report',
        description:
          'Build a report from the selected findings. Choose a date range and template, then continue in Reports.',
        side: 'bottom',
        align: 'end',
      },
      tourAction: TOUR_ACTION_SELECT_FIRST_RISK,
      requiresRisksRow: true,
      requiresRiskSelection: true,
    },
    {
      page: 'risks',
      element: '[data-tour="risks-row-status"]',
      popover: {
        title: 'Row status',
        description:
          'Update remediation status on a single finding without opening detail. Confirmations capture notes when the workflow requires them.',
        side: 'left',
        align: 'start',
      },
      tourAction: TOUR_ACTION_CLEAR_RISK_SELECTION,
      requiresRisksRow: true,
    },
    {
      page: 'riskDetail',
      element: '[data-tour="risk-detail-header"]',
      popover: {
        title: 'Vulnerability detail',
        description:
          'Opening a row shows the full finding: severity, CVSS, status controls, notes, instance data, and remediation guidance.',
        side: 'bottom',
        align: 'start',
      },
      tourAction: TOUR_ACTION_OPEN_FIRST_RISK,
      requiresRisksRow: true,
      requiresOpenRiskDetail: true,
    },
    {
      page: 'riskDetail',
      element: '[data-tour="risk-detail-actions"]',
      popover: {
        title: 'Status and notes',
        description:
          'Change Open / Accept Risk / False Positive / Close here, or add a note. Status changes may require a note.',
        side: 'left',
        align: 'start',
      },
      requiresRisksRow: true,
      requiresOpenRiskDetail: true,
    },
    {
      page: 'riskDetail',
      element: '[data-tour="risk-detail-notes"]',
      popover: {
        title: 'Notes',
        description:
          'Track remediation discussion and status rationale. Latest notes appear first, with earlier history expandable below.',
        side: 'left',
        align: 'start',
      },
      requiresRisksRow: true,
      requiresOpenRiskDetail: true,
    },
    {
      page: 'riskDetail',
      element: '[data-tour="risk-detail-instance"]',
      popover: {
        title: 'Instance',
        description:
          'See hostname, IP, OS, scanner, port, CVSS, and when the finding was detected for this instance.',
        side: 'left',
        align: 'start',
      },
      requiresRisksRow: true,
      requiresOpenRiskDetail: true,
    },
    {
      page: 'riskDetail',
      element: '[data-tour="risk-detail-description"]',
      popover: {
        title: 'Description',
        description:
          'Read the summary and impact so you understand what the vulnerability means for this asset.',
        side: 'left',
        align: 'start',
      },
      requiresRisksRow: true,
      requiresOpenRiskDetail: true,
    },
    {
      page: 'riskDetail',
      element: '[data-tour="risk-detail-technical"]',
      popover: {
        title: 'Technical findings',
        description:
          'Review plugin output, evidence, and other technical detail used to confirm the finding.',
        side: 'left',
        align: 'start',
      },
      requiresRisksRow: true,
      requiresOpenRiskDetail: true,
    },
    {
      page: 'riskDetail',
      element: '[data-tour="risk-detail-solution"]',
      popover: {
        title: 'Solution',
        description:
          'Follow solution type and remediation actions recommended for this vulnerability.',
        side: 'left',
        align: 'start',
      },
      requiresRisksRow: true,
      requiresOpenRiskDetail: true,
    },
    {
      page: 'riskDetail',
      element: '[data-tour="risk-detail-details"]',
      popover: {
        title: 'Vulnerability details',
        description:
          'Additional narrative detail for the finding when the scanner provides it.',
        side: 'left',
        align: 'start',
      },
      requiresRisksRow: true,
      requiresOpenRiskDetail: true,
    },
    {
      page: 'riskDetail',
      element: '[data-tour="risk-detail-references"]',
      popover: {
        title: 'References',
        description:
          'Open CVE and vendor references for deeper research. Use Back when you are ready to return to the list.',
        side: 'left',
        align: 'start',
      },
      requiresRisksRow: true,
      requiresOpenRiskDetail: true,
    },
    {
      page: 'riskDetail',
      element: '[data-tour="risk-detail-back"]',
      popover: {
        title: 'Back to vulnerabilities',
        description:
          'Return to the full findings list without losing your place in the platform navigation.',
        side: 'bottom',
        align: 'start',
      },
      requiresRisksRow: true,
      requiresOpenRiskDetail: true,
    },
    ...buildReportsTourSteps({ navTourAction: TOUR_ACTION_CLOSE_RISK_DETAIL }),
  ];

  if (!isNarrowViewport) {
    steps.push(...buildSettingsTourSteps());
    steps.push(...buildSubscriptionTourSteps());
  }

  steps.push(
    ...buildSupportTourSteps(),
    {
      page: 'dashboard',
      element: '[data-tour="nav-dashboard"]',
      popover: {
        title: 'You are ready',
        description:
          'That covers the main areas of the platform. Return here anytime from Guided tours in your profile menu.',
        side: 'right',
        align: 'start',
      },
      tourStepId: 'extended-done',
    }
  );

  return steps;
}

/**
 * One page/area tour only — progress shows this section's step count, not the whole platform.
 * Ends on Guided tours (profile menu) with a Next section handoff when another section remains.
 */
export function buildSectionTourSteps(
  sectionId,
  { isNarrowViewport = false, essentials = false } = {}
) {
  const section = getTourSectionDef(sectionId);
  if (!section) return [];
  if (section.desktopOnly && isNarrowViewport) return [];

  const pageSet = new Set(section.pages);
  let body = buildExtendedTourSteps({ isNarrowViewport }).filter((step) => pageSet.has(step.page));
  if (body.length === 0) return [];

  if (essentials) {
    body = body.filter((step) => !isDeepTourStep(step)).slice(0, 5);
  }

  const nextSectionId = getNextTourSectionId(sectionId, { isNarrowViewport });
  const nextSection = nextSectionId ? getTourSectionDef(nextSectionId) : null;
  const handoffPage = body[body.length - 1]?.page || section.startPage;

  body.push({
    page: handoffPage,
    element: '[data-tour="tour-help"]',
    tourStepId: 'section-handoff',
    nextSectionId,
    tourAction: TOUR_ACTION_OPEN_PROFILE_MENU,
    requiresProfileMenu: true,
    disableActiveInteraction: false,
    popover: {
      title: nextSection ? `${section.label} tour complete` : 'Section tours complete',
      description: nextSection
        ? `This section is done. Open Guided tours in your profile menu anytime to pick another area, or choose Next section for ${nextSection.label}.`
        : 'This section is done. Open Guided tours in your profile menu anytime to restart Overview or another page tour.',
      side: 'left',
      align: 'start',
    },
  });

  return body;
}

let tourDelayScaleForTests = 1;

/** Test-only: set to `0` so interaction suites can walk every step quickly. */
export function __setTourDelayScaleForTests(scale = 1) {
  tourDelayScaleForTests = Number.isFinite(scale) ? Math.max(0, scale) : 1;
}

function delay(ms) {
  const wait = Math.max(0, Math.round(Number(ms) * tourDelayScaleForTests));
  if (wait === 0) return Promise.resolve();
  return new Promise((resolve) => {
    window.setTimeout(resolve, wait);
  });
}

async function waitForSelector(selector, { timeoutMs = 5000, isCancelled } = {}) {
  const started = Date.now();
  while (Date.now() - started < timeoutMs) {
    if (isCancelled?.()) return null;
    const node = document.querySelector(selector);
    if (node) return node;
    await delay(50);
  }
  if (isCancelled?.()) return null;
  return document.querySelector(selector);
}

async function runTourAction(action) {
  if (!action || typeof window === 'undefined') return;
  if (action === TOUR_ACTION_OPEN_FIRST_ASSET) {
    if (!document.querySelector('[data-tour="assets-detail-modal"]')) {
      window.dispatchEvent(new CustomEvent('xspectre:tour-open-first-asset'));
    }
    await waitForSelector('[data-tour="assets-detail-modal"]', { timeoutMs: 5000 });
    await delay(120);
    return;
  }
  if (action === TOUR_ACTION_CLOSE_ASSET_DETAIL) {
    if (!document.querySelector('[data-tour="assets-detail-modal"]')) return;
    window.dispatchEvent(new CustomEvent('xspectre:tour-close-asset-detail'));
    const started = Date.now();
    while (Date.now() - started < 2500 && document.querySelector('[data-tour="assets-detail-modal"]')) {
      await delay(40);
    }
    return;
  }
  if (action === TOUR_ACTION_SELECT_FIRST_ASSET) {
    window.dispatchEvent(new CustomEvent('xspectre:tour-select-first-asset'));
    await waitForSelector('[data-tour="assets-bulk-actions"]', { timeoutMs: 2500 });
    await delay(80);
    return;
  }
  if (action === TOUR_ACTION_CLEAR_ASSET_SELECTION) {
    window.dispatchEvent(new CustomEvent('xspectre:tour-clear-asset-selection'));
    await delay(80);
    return;
  }
  if (action === TOUR_ACTION_SELECT_FIRST_SCAN) {
    window.dispatchEvent(new CustomEvent('xspectre:tour-select-first-scan'));
    await waitForSelector('[data-tour="scans-bulk-actions"]', { timeoutMs: 2500 });
    await delay(80);
    return;
  }
  if (action === TOUR_ACTION_CLEAR_SCAN_SELECTION) {
    window.dispatchEvent(new CustomEvent('xspectre:tour-clear-scan-selection'));
    await delay(80);
    return;
  }
  if (action === TOUR_ACTION_SELECT_FIRST_RISK) {
    window.dispatchEvent(new CustomEvent('xspectre:tour-select-first-risk'));
    await waitForSelector(
      '[data-tour="risks-bulk-actions"], [data-tour="risks-generate-report"]',
      { timeoutMs: 2500 }
    );
    await delay(80);
    return;
  }
  if (action === TOUR_ACTION_CLEAR_RISK_SELECTION) {
    window.dispatchEvent(new CustomEvent('xspectre:tour-clear-risk-selection'));
    await delay(80);
    return;
  }
  if (action === TOUR_ACTION_OPEN_FIRST_RISK) {
    if (!document.querySelector('[data-tour="risk-detail-page"]')) {
      window.dispatchEvent(new CustomEvent('xspectre:tour-open-first-risk'));
    }
    await waitForRiskDetailReady({ timeoutMs: 8000 });
    await delay(120);
    return;
  }
  if (action === TOUR_ACTION_CLOSE_RISK_DETAIL) {
    if (document.querySelector('[data-tour="risk-detail-page"]')) {
      window.dispatchEvent(new CustomEvent('xspectre:tour-close-risk-detail'));
      await waitForRisksPageReady({ timeoutMs: 5000 });
      await delay(100);
    }
    return;
  }
  if (
    action === TOUR_ACTION_NETWORK_SCOPE_ONPREM ||
    action === TOUR_ACTION_NETWORK_SCOPE_PERIMETER ||
    action === TOUR_ACTION_NETWORK_SCOPE_SCHEDULED
  ) {
    const scope =
      action === TOUR_ACTION_NETWORK_SCOPE_PERIMETER
        ? 'perimeter'
        : action === TOUR_ACTION_NETWORK_SCOPE_SCHEDULED
          ? 'scheduled'
          : 'onprem';
    window.dispatchEvent(
      new CustomEvent('xspectre:tour-network-scope', { detail: { scope } })
    );
    await delay(180);
    await waitForSelector(
      `[data-tour="scans-network-panel-${scope}"], [data-tour="scans-table"]`,
      { timeoutMs: 4000 }
    );
    await delay(80);
    return;
  }
  if (action === TOUR_ACTION_OPEN_REPORT_WIZARD) {
    if (!document.querySelector('[data-tour="reports-wizard-modal"]')) {
      window.dispatchEvent(new CustomEvent('xspectre:tour-open-report-wizard'));
    }
    await waitForSelector('[data-tour="reports-wizard-modal"]', { timeoutMs: 4000 });
    await delay(80);
    return;
  }
  if (action === TOUR_ACTION_CLOSE_REPORT_WIZARD) {
    if (document.querySelector('[data-tour="reports-wizard-modal"]')) {
      window.dispatchEvent(new CustomEvent('xspectre:tour-close-report-wizard'));
      await waitForReportWizardClosed({ timeoutMs: 1200 });
    }
    return;
  }
  if (action === TOUR_ACTION_OPEN_FIRST_REPORT) {
    await runTourAction(TOUR_ACTION_CLOSE_REPORT_WIZARD);
    await waitForSelector('[data-tour="reports-page"], [data-tour="report-detail-page"]', {
      timeoutMs: 2000,
    });
    if (!document.querySelector('[data-tour="report-detail-page"]')) {
      if (!document.querySelector('[data-tour="reports-row"]')) {
        await waitForSelector('[data-tour="reports-row"]', { timeoutMs: 2500 });
      }
      window.dispatchEvent(new CustomEvent('xspectre:tour-open-first-report'));
    }
    await waitForReportDetailReady({ timeoutMs: 6000 });
    await delay(80);
    return;
  }
  if (action === TOUR_ACTION_CLOSE_REPORT_DETAIL) {
    if (document.querySelector('[data-tour="report-detail-page"]')) {
      window.dispatchEvent(new CustomEvent('xspectre:tour-close-report-detail'));
      await waitForReportsPageReady({ timeoutMs: 2500 });
    }
    return;
  }
  if (action.startsWith(TOUR_ACTION_REPORT_TAB_PREFIX)) {
    const tab = action.slice(TOUR_ACTION_REPORT_TAB_PREFIX.length);
    window.dispatchEvent(new CustomEvent('xspectre:tour-report-tab', { detail: { tab } }));
    await delay(40);
    return;
  }
  if (action.startsWith(TOUR_ACTION_CONNECTORS_TAB_PREFIX)) {
    const tab = action.slice(TOUR_ACTION_CONNECTORS_TAB_PREFIX.length);
    window.dispatchEvent(new CustomEvent('xspectre:tour-connectors-tab', { detail: { tab } }));
    await waitForSelector('[data-tour="settings-connectors-table"]', { timeoutMs: 4000 });
    await delay(80);
    return;
  }
  if (action.startsWith(TOUR_ACTION_DEPLOYMENT_TAB_PREFIX)) {
    const tab = action.slice(TOUR_ACTION_DEPLOYMENT_TAB_PREFIX.length);
    window.dispatchEvent(new CustomEvent('xspectre:tour-deployment-tab', { detail: { tab } }));
    const selector =
      tab === 'tokens'
        ? '[data-tour="settings-deployment-tokens"]'
        : tab === 'attempts'
          ? '[data-tour="settings-deployment-attempts"]'
          : '[data-tour="settings-deployment-general"]';
    await waitForSelector(selector, { timeoutMs: 4000 });
    await delay(80);
    return;
  }
  if (action === TOUR_ACTION_OPEN_CONNECTOR_CONFIG || action === TOUR_ACTION_OPEN_NETWORK_CONNECTOR_CONFIG) {
    const tab = action === TOUR_ACTION_OPEN_NETWORK_CONNECTOR_CONFIG ? 'network' : 'web';
    window.dispatchEvent(new CustomEvent('xspectre:tour-connectors-tab', { detail: { tab } }));
    await waitForSelector('[data-tour="settings-connectors-table"]', { timeoutMs: 4000 });
    await delay(80);
    if (!document.querySelector('[data-tour="settings-connectors-modal"]')) {
      window.dispatchEvent(new CustomEvent('xspectre:tour-open-connector-config'));
    }
    await waitForSelector('[data-tour="settings-connectors-modal"]', { timeoutMs: 5000 });
    await delay(80);
    return;
  }
  if (action === TOUR_ACTION_CLOSE_CONNECTOR_CONFIG) {
    if (document.querySelector('[data-tour="settings-connectors-modal"]')) {
      window.dispatchEvent(new CustomEvent('xspectre:tour-close-connector-config'));
      const started = Date.now();
      while (Date.now() - started < 2500 && document.querySelector('[data-tour="settings-connectors-modal"]')) {
        await delay(40);
      }
    }
    return;
  }
  if (action === TOUR_ACTION_SELECT_FIRST_ATTEMPT) {
    window.dispatchEvent(new CustomEvent('xspectre:tour-deployment-tab', { detail: { tab: 'attempts' } }));
    await waitForSelector('[data-tour="settings-deployment-attempts"]', { timeoutMs: 4000 });
    window.dispatchEvent(new CustomEvent('xspectre:tour-select-first-attempt'));
    await waitForSelector('[data-tour="settings-deployment-attempt-detail"]', { timeoutMs: 4000 });
    const started = Date.now();
    while (Date.now() - started < 2500) {
      if (document.querySelector('[data-tour="settings-deployment-attempt-detail"][data-filled="true"]')) {
        break;
      }
      await delay(50);
    }
    await delay(80);
    return;
  }
  if (action === TOUR_ACTION_OPEN_FIRST_SUBSCRIPTION_ORG) {
    if (!document.querySelector('[data-tour="subscription-detail-page"]')) {
      window.dispatchEvent(new CustomEvent('xspectre:tour-open-first-subscription-org'));
    }
    await waitForSubscriptionDetailReady({ timeoutMs: 12000 });
    await delay(80);
    return;
  }
  if (action === TOUR_ACTION_CLOSE_SUBSCRIPTION_ORG) {
    if (document.querySelector('[data-tour="subscription-detail-page"]')) {
      window.dispatchEvent(new CustomEvent('xspectre:tour-close-subscription-org'));
      await waitForSubscriptionPageReady({ timeoutMs: 12000 });
    }
    return;
  }
  if (action.startsWith(TOUR_ACTION_SUBSCRIPTION_TAB_PREFIX)) {
    const tab = action.slice(TOUR_ACTION_SUBSCRIPTION_TAB_PREFIX.length);
    if (!document.querySelector('[data-tour="subscription-detail-page"]')) {
      await runTourAction(TOUR_ACTION_OPEN_FIRST_SUBSCRIPTION_ORG);
    }
    window.dispatchEvent(new CustomEvent('xspectre:tour-subscription-tab', { detail: { tab } }));
    await delay(80);
    return;
  }
  if (action.startsWith(TOUR_ACTION_SUPPORT_TAB_PREFIX)) {
    const tab = action.slice(TOUR_ACTION_SUPPORT_TAB_PREFIX.length);
    window.dispatchEvent(new CustomEvent('xspectre:tour-support-tab', { detail: { tab } }));
    const selector =
      tab === 'create'
        ? '[data-tour="support-create-form"]'
        : '[data-tour="support-ticket-list"], [data-tour="support-tickets-panel"]';
    await waitForSelector(selector, { timeoutMs: 4000 });
    await delay(80);
    return;
  }
  if (action === TOUR_ACTION_SELECT_FIRST_TICKET) {
    window.dispatchEvent(new CustomEvent('xspectre:tour-support-tab', { detail: { tab: 'tickets' } }));
    await waitForSelector('[data-tour="support-tickets-panel"]', { timeoutMs: 4000 });
    window.dispatchEvent(new CustomEvent('xspectre:tour-select-first-ticket'));
    await waitForSelector('[data-tour="support-ticket-detail"]', { timeoutMs: 4000 });
    const started = Date.now();
    while (Date.now() - started < 2500) {
      if (document.querySelector('[data-tour="support-ticket-detail"][data-filled="true"]')) {
        break;
      }
      await delay(50);
    }
    await delay(80);
    return;
  }
  if (action === TOUR_ACTION_OPEN_NEW_SCAN) {
    if (!document.querySelector('[data-tour="new-scan-modal"]')) {
      window.dispatchEvent(new CustomEvent('xspectre:tour-open-new-scan'));
    }
    await waitForSelector('[data-tour="new-scan-modal"]', { timeoutMs: 4000 });
    await delay(80);
    return;
  }
  if (action === TOUR_ACTION_CLOSE_NEW_SCAN) {
    if (document.querySelector('[data-tour="new-scan-modal"]')) {
      window.dispatchEvent(new CustomEvent('xspectre:tour-close-new-scan'));
      const started = Date.now();
      while (Date.now() - started < 2500 && document.querySelector('[data-tour="new-scan-modal"]')) {
        await delay(40);
      }
    }
    return;
  }
  if (action === TOUR_ACTION_OPEN_PROFILE_MENU) {
    window.dispatchEvent(new CustomEvent('xspectre:tour-open-profile-menu'));
    await waitForSelector('[data-tour="profile-menu"]', { timeoutMs: 2500 });
    await delay(80);
    return;
  }
  if (action === TOUR_ACTION_CLOSE_PROFILE_MENU) {
    if (!document.querySelector('[data-tour="profile-menu"]')) return;
    window.dispatchEvent(new CustomEvent('xspectre:tour-close-profile-menu'));
    await delay(40);
    return;
  }
  if (action.startsWith(TOUR_ACTION_PROFILE_TAB_PREFIX)) {
    const tab = action.slice(TOUR_ACTION_PROFILE_TAB_PREFIX.length);
    window.dispatchEvent(new CustomEvent('xspectre:tour-profile-tab', { detail: { tab } }));
    await delay(80);
    return;
  }
}

async function prepareTourStep(step, { timeoutMs = 8000, isCancelled } = {}) {
  if (!step) return null;
  if (isCancelled?.()) return null;

  const stayingOnReportDetail = Boolean(step.requiresOpenReportDetail);
  const stayingOnReportWizard = Boolean(step.requiresReportWizard);

  // Keep the page usable when moving off detail steps (especially Back).
  if (!step.requiresOpenAssetDetail) {
    await runTourAction(TOUR_ACTION_CLOSE_ASSET_DETAIL);
    if (isCancelled?.()) return null;
  }
  if (!step.requiresOpenRiskDetail) {
    await runTourAction(TOUR_ACTION_CLOSE_RISK_DETAIL);
    if (isCancelled?.()) return null;
  }
  // Defer closing the report wizard until the next target is resolved — closing
  // first unmounts the active highlight and freezes Next (Report objective).
  if (!stayingOnReportDetail) {
    await runTourAction(TOUR_ACTION_CLOSE_REPORT_DETAIL);
    if (isCancelled?.()) return null;
  }
  if (!step.requiresConnectorModal) {
    await runTourAction(TOUR_ACTION_CLOSE_CONNECTOR_CONFIG);
    if (isCancelled?.()) return null;
  }
  if (!step.requiresOpenSubscriptionDetail) {
    await runTourAction(TOUR_ACTION_CLOSE_SUBSCRIPTION_ORG);
    if (isCancelled?.()) return null;
  }
  if (!step.requiresNewScanModal) {
    await runTourAction(TOUR_ACTION_CLOSE_NEW_SCAN);
    if (isCancelled?.()) return null;
  }
  if (!step.requiresProfileMenu) {
    await runTourAction(TOUR_ACTION_CLOSE_PROFILE_MENU);
    if (isCancelled?.()) return null;
  }

  if (step.tourAction) {
    await runTourAction(step.tourAction);
  } else if (step.requiresOpenAssetDetail) {
    await runTourAction(TOUR_ACTION_OPEN_FIRST_ASSET);
  } else if (step.requiresOpenRiskDetail) {
    await runTourAction(TOUR_ACTION_OPEN_FIRST_RISK);
  } else if (step.requiresOpenReportDetail) {
    await runTourAction(TOUR_ACTION_OPEN_FIRST_REPORT);
  } else if (step.requiresReportWizard) {
    await runTourAction(TOUR_ACTION_OPEN_REPORT_WIZARD);
  }
  if (isCancelled?.()) return null;

  // Subscription list remounts after fetch — wait before resolving step targets.
  if (step.page === 'subscription' && !step.requiresOpenSubscriptionDetail) {
    await waitForSubscriptionPageReady({ timeoutMs: Math.max(timeoutMs, 12000), isCancelled });
    if (isCancelled?.()) return null;
  }
  if (step.page === 'subscriptionOrgDetail' || step.requiresOpenSubscriptionDetail) {
    if (document.querySelector('[data-tour="subscription-detail-page"]')) {
      await waitForSubscriptionDetailReady({ timeoutMs: Math.max(timeoutMs, 12000), isCancelled });
      if (isCancelled?.()) return null;
    }
  }

  if (!step.element) {
    if (!stayingOnReportWizard) {
      await runTourAction(TOUR_ACTION_CLOSE_REPORT_WIZARD);
      if (isCancelled?.()) return null;
    }
    return true;
  }

  // Resolve the next highlight while the wizard can still be open (handoff target
  // lives outside the modal). Then close the wizard and re-confirm the node.
  let node = await waitForSelector(step.element, { timeoutMs, isCancelled });
  if (isCancelled?.()) return null;

  if (!stayingOnReportWizard) {
    await runTourAction(TOUR_ACTION_CLOSE_REPORT_WIZARD);
    if (isCancelled?.()) return null;
    await delay(60);
    node = await waitForSelector(step.element, { timeoutMs: Math.min(timeoutMs, 3000), isCancelled });
    if (isCancelled?.()) return null;
  }

  if (node && typeof node.scrollIntoView === 'function') {
    try {
      const centerStep =
        step.requiresOpenRiskDetail ||
        step.advanceOnClick ||
        step.element === '[data-tour="assets-row-open"]' ||
        step.element === '[data-tour="assets-row"]';
      node.scrollIntoView({
        block: centerStep ? 'center' : 'nearest',
        inline: 'nearest',
        behavior: 'auto',
      });
    } catch (_) {
      // Ignore scroll failures; driver will still attempt to highlight.
    }
    await delay(80);
  }
  if (isCancelled?.()) return null;
  return node;
}

async function waitForAssetsInventoryReady({ timeoutMs = 8000 } = {}) {
  await waitForSelector('[data-tour="assets-inventory"]', { timeoutMs });
  const started = Date.now();
  while (Date.now() - started < timeoutMs) {
    if (
      document.querySelector(
        '.assets-empty-cell, [data-tour="assets-row"], .assets-row-clickable'
      )
    ) {
      return true;
    }
    if (!document.querySelector('.risks-page-loading-text')) {
      return Boolean(document.querySelector('[data-tour="assets-table"]'));
    }
    await delay(50);
  }
  return Boolean(document.querySelector('[data-tour="assets-inventory"]'));
}

async function waitForScansPageReady({ timeoutMs = 8000 } = {}) {
  await waitForSelector('[data-tour="scans-page"]', { timeoutMs });
  const started = Date.now();
  while (Date.now() - started < timeoutMs) {
    if (document.querySelector('[data-tour="scans-table"], [data-tour="scans-row"], .assets-empty-cell')) {
      return true;
    }
    if (!isScansTableLoading()) {
      return Boolean(document.querySelector('[data-tour="scans-page"]'));
    }
    await delay(50);
  }
  return Boolean(document.querySelector('[data-tour="scans-page"]'));
}

function isSubscriptionPageLoading() {
  const page = document.querySelector('[data-tour="subscription-page"]');
  if (!page) return false;
  // Full-page loader: page shell exists, org table not mounted yet.
  if (!document.querySelector('[data-tour="subscription-org-table"]')) {
    return Boolean(page.querySelector('.blue-x-loader, .dashboard-data-loader, .risks-page-loading-text'));
  }
  return Boolean(
    document.querySelector(
      '[data-tour="subscription-org-table"] .risks-page-table-loading, [data-tour="subscription-org-table"] .blue-x-loader'
    )
  );
}

/** Wait until billing orgs finished loading — starting earlier drops table steps and Next skips them. */
async function waitForSubscriptionPageReady({ timeoutMs = 12000, isCancelled } = {}) {
  await waitForSelector('[data-tour="subscription-page"]', { timeoutMs, isCancelled });
  if (isCancelled?.()) return false;
  const started = Date.now();
  while (Date.now() - started < timeoutMs) {
    if (isCancelled?.()) return false;
    const table = document.querySelector('[data-tour="subscription-org-table"]');
    if (table && !table.querySelector('.risks-page-table-loading, .blue-x-loader')) {
      return true;
    }
    // Error / empty states also mean the list finished loading.
    if (
      document.querySelector(
        '[data-tour="subscription-page"] .profile-settings-error, [data-tour="subscription-page"] .assets-empty-cell'
      )
    ) {
      return true;
    }
    await delay(50);
  }
  return Boolean(document.querySelector('[data-tour="subscription-org-table"], [data-tour="subscription-page"]'));
}

async function waitForSubscriptionDetailReady({ timeoutMs = 12000, isCancelled } = {}) {
  await waitForSelector('[data-tour="subscription-detail-page"]', { timeoutMs, isCancelled });
  if (isCancelled?.()) return false;
  const started = Date.now();
  while (Date.now() - started < timeoutMs) {
    if (isCancelled?.()) return false;
    if (document.querySelector('[data-tour="subscription-detail-tabs"]')) {
      // Tabs are up; wait out the initial "Loading organisation/subscription" shells when present.
      const loading = document.querySelector(
        '[data-tour="subscription-detail-page"] .blue-x-loader, [data-tour="subscription-detail-page"] .risks-page-loading-text'
      );
      if (!loading) return true;
      // Some panels load lazily — tabs alone are enough to continue the tour.
      if (document.querySelector('[data-tour="subscription-plan-overview"], [data-tour="subscription-detail-tabs"]')) {
        return true;
      }
    }
    await delay(50);
  }
  return Boolean(document.querySelector('[data-tour="subscription-detail-page"]'));
}

async function waitForRisksPageReady({ timeoutMs = 8000 } = {}) {
  await waitForSelector('[data-tour="risks-page"]', { timeoutMs });
  const started = Date.now();
  while (Date.now() - started < timeoutMs) {
    if (
      document.querySelector(
        '[data-tour="risks-table"], [data-tour="risks-row"], .assets-empty-cell'
      )
    ) {
      return true;
    }
    if (!isRisksTableLoading()) {
      return Boolean(document.querySelector('[data-tour="risks-page"]'));
    }
    await delay(50);
  }
  return Boolean(document.querySelector('[data-tour="risks-page"]'));
}

async function waitForRiskDetailReady({ timeoutMs = 8000 } = {}) {
  await waitForSelector('[data-tour="risk-detail-page"]', { timeoutMs });
  const started = Date.now();
  while (Date.now() - started < timeoutMs) {
    if (document.querySelector('.assets-detail-loading')) {
      await delay(50);
      continue;
    }
    if (document.querySelector('[data-tour="risk-detail-header"]')) {
      return true;
    }
    await delay(50);
  }
  return Boolean(document.querySelector('[data-tour="risk-detail-page"]'));
}

/** Navigate between extended-tour pages without breaking risk detail (id-specific route). */
async function navigateTourPageChange(fromPage, toPage, onNavigateToPage, { isCancelled } = {}) {
  if (!toPage || !tourPagesDiffer(fromPage, toPage)) return;
  if (isCancelled?.()) return;

  if (toPage === 'riskDetail') {
    await runTourAction(TOUR_ACTION_OPEN_FIRST_RISK);
    return;
  }

  if (fromPage === 'riskDetail') {
    await runTourAction(TOUR_ACTION_CLOSE_RISK_DETAIL);
    if (isCancelled?.()) return;
    if (toPage === 'risks') {
      await waitForRisksPageReady();
      if (isCancelled?.()) return;
      await waitForSelector(
        '[data-tour="risks-row"], [data-tour="risks-table"], .assets-empty-cell',
        { timeoutMs: 5000, isCancelled }
      );
      await delay(120);
      return;
    }
  }

  if (toPage === 'reportDetail') {
    await runTourAction(TOUR_ACTION_OPEN_FIRST_REPORT);
    return;
  }

  if (fromPage === 'reportDetail') {
    await runTourAction(TOUR_ACTION_CLOSE_REPORT_DETAIL);
    if (isCancelled?.()) return;
    if (toPage === 'reports') {
      await waitForReportsPageReady();
      await delay(120);
      return;
    }
  }

  if (toPage === 'subscriptionOrgDetail') {
    await runTourAction(TOUR_ACTION_OPEN_FIRST_SUBSCRIPTION_ORG);
    return;
  }

  if (fromPage === 'subscriptionOrgDetail') {
    await runTourAction(TOUR_ACTION_CLOSE_SUBSCRIPTION_ORG);
    if (isCancelled?.()) return;
    if (toPage === 'subscription') {
      await waitForSubscriptionPageReady({ timeoutMs: 12000, isCancelled });
      await delay(80);
      return;
    }
  }

  if (typeof onNavigateToPage === 'function') {
    if (isCancelled?.()) return;
    onNavigateToPage(toPage);
    await waitForTourPage(toPage, { isCancelled });
  }
}

async function waitForTourPage(page, { timeoutMs = 8000, isCancelled } = {}) {
  const normalized = normalizeTourPage(page);
  if (normalized === 'targets') return waitForAssetsInventoryReady({ timeoutMs });
  if (normalized === 'scans') return waitForScansPageReady({ timeoutMs });
  if (normalized === 'risks') return waitForRisksPageReady({ timeoutMs });
  if (normalized === 'riskDetail') return waitForRiskDetailReady({ timeoutMs });
  if (normalized === 'reports') return waitForReportsPageReady({ timeoutMs });
  if (normalized === 'reportDetail') return waitForReportDetailReady({ timeoutMs });
  if (normalized === 'settingsConnectors') {
    return waitForSelector('[data-tour="settings-connectors-table"], [data-tour="settings-connectors-tabs"]', {
      timeoutMs,
      isCancelled,
    });
  }
  if (normalized === 'settingsDeployment') {
    return waitForSelector('[data-tour="settings-deployment-tabs"], [data-tour="settings-deployment-general"]', {
      timeoutMs,
      isCancelled,
    });
  }
  if (normalized === 'settingsManageUsers') {
    return waitForSelector('[data-tour="settings-users-table"], [data-tour="settings-users-add"]', {
      timeoutMs,
      isCancelled,
    });
  }
  if (normalized === 'settingsUsersOrgs') {
    return waitForSelector('[data-tour="settings-orgs-table"], [data-tour="settings-orgs-add"]', {
      timeoutMs,
      isCancelled,
    });
  }
  if (normalized === 'settingsPartners') {
    return waitForSelector('[data-tour="settings-partners-table"], [data-tour="settings-partners-add"]', {
      timeoutMs,
      isCancelled,
    });
  }
  if (normalized === 'subscription') {
    return waitForSubscriptionPageReady({ timeoutMs: Math.max(timeoutMs, 12000), isCancelled });
  }
  if (normalized === 'subscriptionOrgDetail') {
    return waitForSubscriptionDetailReady({ timeoutMs: Math.max(timeoutMs, 12000), isCancelled });
  }
  if (normalized === 'support') {
    return waitForSelector('[data-tour="support-page"], [data-tour="support-tabs"]', { timeoutMs });
  }
  if (normalized === 'profileSettings') {
    return waitForSelector('[data-tour="profile-settings-page"], [data-tour="profile-settings-tabs"]', {
      timeoutMs,
    });
  }
  if (normalized === 'dashboard') {
    return waitForSelector('[data-tour="security-overview"], [data-tour="scope-controls"]', { timeoutMs });
  }
  if (page) await delay(220);
  return true;
}

async function waitForReportsPageReady({ timeoutMs = 8000 } = {}) {
  await waitForSelector('[data-tour="reports-page"]', { timeoutMs });
  const started = Date.now();
  while (Date.now() - started < timeoutMs) {
    if (
      document.querySelector(
        '[data-tour="reports-table"], [data-tour="reports-row"], .assets-empty-cell'
      )
    ) {
      return true;
    }
    if (!document.querySelector('.risks-page-loading-text')) {
      return Boolean(document.querySelector('[data-tour="reports-page"]'));
    }
    await delay(50);
  }
  return Boolean(document.querySelector('[data-tour="reports-page"]'));
}

async function waitForReportDetailReady({ timeoutMs = 8000 } = {}) {
  await waitForSelector('[data-tour="report-detail-page"]', { timeoutMs });
  const started = Date.now();
  while (Date.now() - started < timeoutMs) {
    if (document.querySelector('[data-tour="report-detail-tabs"]')) {
      return true;
    }
    await delay(50);
  }
  return Boolean(document.querySelector('[data-tour="report-detail-page"]'));
}

async function waitForReportWizardClosed({ timeoutMs = 2500 } = {}) {
  const started = Date.now();
  while (Date.now() - started < timeoutMs) {
    if (!document.querySelector('[data-tour="reports-wizard-modal"]')) return true;
    await delay(40);
  }
  return !document.querySelector('[data-tour="reports-wizard-modal"]');
}

function hasAssetsInventoryRows() {
  return Boolean(document.querySelector('[data-tour="assets-row"], .assets-row-clickable'));
}

function hasScansRows() {
  return Boolean(document.querySelector('[data-tour="scans-row"]'));
}

function hasRisksRows() {
  return Boolean(document.querySelector('[data-tour="risks-row"]'));
}

function hasOpenRiskDetail() {
  return Boolean(document.querySelector('[data-tour="risk-detail-page"]'));
}

function hasReportsRows() {
  return Boolean(document.querySelector('[data-tour="reports-row"]'));
}

function hasOpenReportDetail() {
  return Boolean(document.querySelector('[data-tour="report-detail-page"]'));
}


function isScansTableLoading() {
  return Boolean(
    document.querySelector(
      '[data-tour="scans-page"] [aria-busy="true"], .scans-table-card [aria-busy="true"]'
    )
  );
}

function isRisksTableLoading() {
  return Boolean(
    document.querySelector(
      '[data-tour="risks-page"] [aria-busy="true"], .risks-page-table-loading'
    )
  );
}

/**
 * Keep cross-page steps even when their DOM is not mounted yet (persistent Dashboard shell).
 * Only drop current-page steps that are truly unavailable (e.g. no rows / no pagination).
 */
function shouldKeepTourStep(step, { activePage } = {}) {
  if (!step) return false;

  const onStepPage = !step.page || tourPageMatchesActive(step.page, activePage);

  if (!onStepPage) {
    // Future/past route steps stay in the sequence; navigation materializes them.
    return true;
  }

  if (step.requiresAssetsRow && !hasAssetsInventoryRows()) return false;
  if (step.requiresOpenAssetDetail && !hasAssetsInventoryRows()) return false;
  if (step.requiresOpenRiskDetail) {
    if (hasOpenRiskDetail()) return true;
    if (isRisksTableLoading()) return true;
    return hasRisksRows();
  }
  if (step.requiresScansRow || step.requiresScanSelection) {
    if (isScansTableLoading()) return true;
    if (step.requiresScanSelection) return hasScansRows();
    return hasScansRows();
  }
  if (step.requiresRisksRow || step.requiresRiskSelection) {
    if (hasOpenRiskDetail()) return true;
    if (isRisksTableLoading()) return true;
    if (step.requiresRiskSelection) return hasRisksRows();
    return hasRisksRows();
  }
  if (step.requiresReportsRow || step.requiresOpenReportDetail) {
    if (hasOpenReportDetail()) return true;
    if (document.querySelector('[data-tour="reports-page"] .risks-page-loading-text')) return true;
    return hasReportsRows();
  }
  // Keep list steps while billing orgs are still fetching — dropping them made Next skip ahead.
  if (
    step.page === 'subscription' &&
    (step.element === '[data-tour="subscription-org-table"]' ||
      step.element === '[data-tour="subscription-view-plans"]' ||
      step.element === '[data-tour="subscription-org-search"]')
  ) {
    if (isSubscriptionPageLoading()) return true;
  }
  if (step.requiresNewScanModal || step.requiresProfileMenu) return true;
  if (step.requiresReportWizard) {
    // Keep wizard follow-up steps even before the modal is open — an earlier
    // open-report-wizard action materializes them. Dropping them caused Next to
    // jump from the wizard intro straight into report detail and break the tour.
    return true;
  }
  if (
    step.requiresConnectorConfigure ||
    step.requiresConnectorModal ||
    step.requiresAttemptDetail ||
    step.requiresOpenSubscriptionDetail
  ) {
    return true;
  }
  if (step.requiresAssetSelection) return hasAssetsInventoryRows();
  if (!step.element) return true;
  if (
    step.tourAction === TOUR_ACTION_SELECT_FIRST_ASSET ||
    step.tourAction === TOUR_ACTION_SELECT_FIRST_SCAN ||
    step.tourAction === TOUR_ACTION_SELECT_FIRST_RISK ||
    step.tourAction === TOUR_ACTION_OPEN_FIRST_ASSET ||
    step.tourAction === TOUR_ACTION_OPEN_FIRST_RISK ||
    step.tourAction === TOUR_ACTION_OPEN_FIRST_REPORT ||
    step.tourAction === TOUR_ACTION_OPEN_REPORT_WIZARD ||
    step.tourAction === TOUR_ACTION_NETWORK_SCOPE_ONPREM ||
    step.tourAction === TOUR_ACTION_NETWORK_SCOPE_PERIMETER ||
    step.tourAction === TOUR_ACTION_NETWORK_SCOPE_SCHEDULED ||
    String(step.tourAction || '').startsWith(TOUR_ACTION_CONNECTORS_TAB_PREFIX) ||
    String(step.tourAction || '').startsWith(TOUR_ACTION_DEPLOYMENT_TAB_PREFIX) ||
    step.tourAction === TOUR_ACTION_OPEN_CONNECTOR_CONFIG ||
    step.tourAction === TOUR_ACTION_OPEN_NETWORK_CONNECTOR_CONFIG ||
    step.tourAction === TOUR_ACTION_SELECT_FIRST_ATTEMPT ||
    step.tourAction === TOUR_ACTION_OPEN_FIRST_SUBSCRIPTION_ORG ||
    String(step.tourAction || '').startsWith(TOUR_ACTION_SUBSCRIPTION_TAB_PREFIX) ||
    String(step.tourAction || '').startsWith(TOUR_ACTION_SUPPORT_TAB_PREFIX) ||
    step.tourAction === TOUR_ACTION_SELECT_FIRST_TICKET ||
    step.tourAction === TOUR_ACTION_OPEN_NEW_SCAN ||
    step.tourAction === TOUR_ACTION_OPEN_PROFILE_MENU ||
    String(step.tourAction || '').startsWith(TOUR_ACTION_PROFILE_TAB_PREFIX)
  ) {
    return true;
  }
  return Boolean(document.querySelector(step.element));
}

/** Map tour step pages onto DashboardPage activePage keys (route remounts). */
function normalizeTourPage(page) {
  if (page === 'scansWeb' || page === 'scansNetwork' || page === 'scansPerimeter' || page === 'scans') return 'scans';
  // Detail routes still report activePage `reports` / `risks` from pathToPage.
  if (page === 'reportDetail') return 'reports';
  if (page === 'riskDetail') return 'risks';
  return page || null;
}

function tourPageMatchesActive(tourPage, activePage) {
  if (!tourPage) return true;
  const target = normalizeTourPage(tourPage);
  const current = normalizeTourPage(activePage);
  return Boolean(target && current && target === current);
}

/** Raw page key changed (e.g. scansWeb → scansNetwork) even if activePage stays `scans`. */
function tourPagesDiffer(fromPage, toPage) {
  if (!toPage) return false;
  if (!fromPage) return true;
  return String(fromPage) !== String(toPage);
}

/** Always re-drive after async prepare — moveNext keeps stale stage/popover geometry. */
function tourStepNeedsFreshDrive(_fromStep, _toStep) {
  return true;
}

export function createTourDriverConfig({
  steps,
  storageKey,
  mode,
  sectionId = null,
  focusedBeforeTour,
  finishCleanup,
  driverRef,
  generationRef,
  onNavigateToPage,
  onRequestNextSection,
  onTourCompleted,
  animate = null,
  suppressCleanupRef = null,
}) {
  const isSectionMode = mode === TOUR_MODE_SECTION || mode === TOUR_MODE_EXTENDED;
  const progressStatus = () => (isSectionMode ? 'section_in_progress' : 'in_progress');
  const completedOptionId = isSectionMode ? sectionId : 'overview';
  const persistProgress = (status, stepIndex, element) =>
    writeDashboardTourProgress(storageKey, status, stepIndex, mode, element, { sectionId });
  const completeTour = (status, stepIndex, element) => {
    persistProgress(status, stepIndex, element);
    if (completedOptionId && typeof onTourCompleted === 'function') {
      onTourCompleted(completedOptionId);
    } else if (completedOptionId) {
      markTourOptionCompleted(storageKey, completedOptionId);
    }
  };
  let navBusy = false;

  const isCurrentGeneration = (generation) => generationRef.current === generation;

  const setNavButtonsBusy = (busy) => {
    document
      .querySelectorAll(
        '.xspectre-dashboard-tour .driver-popover-next-btn, .xspectre-dashboard-tour .driver-popover-prev-btn'
      )
      .forEach((btn) => {
        if (busy) {
          btn.setAttribute('disabled', 'true');
          return;
        }
        // Keep Back disabled only on the first step.
        if (
          btn.classList.contains('driver-popover-prev-btn') &&
          (driverRef.current?.getActiveIndex?.() ?? 0) <= 0
        ) {
          btn.setAttribute('disabled', 'true');
          return;
        }
        btn.removeAttribute('disabled');
      });
  };

  const hardStopTour = (tourDriver) => {
    generationRef.current += 1;
    navBusy = false;
    try {
      tourDriver?.destroy?.();
    } catch (_) {
      /* ignore */
    }
    driverRef.current = null;
    forceRemoveDriverDom();
    if (!suppressCleanupRef?.current) {
      finishCleanup();
    }
  };

  const preferAnimate =
    animate == null
      ? !window.matchMedia?.('(prefers-reduced-motion: reduce)').matches
      : Boolean(animate);

  return {
    steps,
    animate: preferAnimate,
    // Keep stage/popover moves snappy — long transitions make Next feel dead.
    duration: preferAnimate ? 220 : 0,
    smoothScroll: true,
    allowClose: true,
    allowScroll: true,
    allowKeyboardControl: true,
    disableActiveInteraction: true,
    overlayClickBehavior: () => {},
    overlayColor: '#000000',
    overlayOpacity: 0.62,
    stagePadding: 8,
    stageRadius: 14,
    popoverClass: 'xspectre-dashboard-tour',
    popoverOffset: 18,
    showProgress: true,
    progressText: '{{current}} of {{total}}',
    nextBtnText: 'Next',
    prevBtnText: 'Back',
    doneBtnText: 'Finish',
    onNextClick: async () => {
      if (navBusy) return;
      const tourDriver = driverRef.current;
      if (!tourDriver) return;
      const generation = generationRef.current;
      const isCancelled = () => !isCurrentGeneration(generation);
      const driveTo = (index) => {
        const live = driverRef.current || tourDriver;
        try {
          live?.drive?.(index);
        } catch (_) {
          /* driver can throw if the prior highlight node was unmounted */
        }
      };
      navBusy = true;
      setNavButtonsBusy(true);
      try {
        const activeIndex = tourDriver.getActiveIndex?.() ?? 0;
        const nextIndex = activeIndex + 1;
        if (nextIndex >= steps.length) {
          completeTour('completed', activeIndex, steps[activeIndex]?.element || null);
          hardStopTour(tourDriver);
          return;
        }

        const currentStep = steps[activeIndex];
        const nextStep = steps[nextIndex];
        persistProgress(progressStatus(), nextIndex, nextStep?.element || null);

        const leavingWizard =
          Boolean(currentStep?.requiresReportWizard) && !nextStep?.requiresReportWizard;

        // Move the highlight off the wizard BEFORE unmounting it — otherwise
        // driver.js keeps a dead active element and Next never advances.
        if (leavingWizard && nextStep?.element) {
          const nextNode = await waitForSelector(nextStep.element, {
            timeoutMs: 4000,
            isCancelled,
          });
          if (isCancelled()) return;
          if (nextNode) {
            driveTo(nextIndex);
            await delay(50);
          }
          await runTourAction(TOUR_ACTION_CLOSE_REPORT_WIZARD);
          if (isCancelled()) return;
          await delay(60);
          // Refresh stage now that the modal is gone.
          driveTo(nextIndex);
          return;
        }

        if (nextStep?.page && tourPagesDiffer(currentStep?.page, nextStep?.page)) {
          await navigateTourPageChange(currentStep?.page, nextStep.page, onNavigateToPage, {
            isCancelled,
          });
          if (isCancelled()) return;
        }

        const prepared = await prepareTourStep(nextStep, { isCancelled });
        if (isCancelled()) return;
        if (nextStep?.element && !prepared) {
          let skipTo = nextIndex + 1;
          while (skipTo < steps.length) {
            if (isCancelled()) return;
            const candidate = steps[skipTo];
            if (candidate?.page) {
              await navigateTourPageChange(
                nextStep?.page || currentStep?.page,
                candidate.page,
                onNavigateToPage,
                { isCancelled }
              );
              if (isCancelled()) return;
            }
            const ok = await prepareTourStep(candidate, { isCancelled });
            if (isCancelled()) return;
            if (!candidate?.element || ok) {
              persistProgress(progressStatus(), skipTo, candidate?.element || null);
              driveTo(skipTo);
              return;
            }
            skipTo += 1;
          }
          completeTour('completed', activeIndex, steps[activeIndex]?.element || null);
          hardStopTour(tourDriver);
          return;
        }

        // Fresh drive after prepare so highlight/popover track the live element.
        void tourStepNeedsFreshDrive(currentStep, nextStep);
        driveTo(nextIndex);
      } finally {
        navBusy = false;
        // Always unlock — gating on driverRef left Report objective permanently stuck.
        setNavButtonsBusy(false);
      }
    },
    onPrevClick: async () => {
      if (navBusy) return;
      const tourDriver = driverRef.current;
      if (!tourDriver) return;
      const generation = generationRef.current;
      const isCancelled = () => !isCurrentGeneration(generation);
      const driveTo = (index) => {
        const live = driverRef.current || tourDriver;
        try {
          live?.drive?.(index);
        } catch (_) {
          /* ignore */
        }
      };
      navBusy = true;
      setNavButtonsBusy(true);
      try {
        const activeIndex = tourDriver.getActiveIndex?.() ?? 0;
        if (activeIndex <= 0) return;
        const prevIndex = activeIndex - 1;
        const currentStep = steps[activeIndex];
        const prevStep = steps[prevIndex];
        persistProgress(progressStatus(), prevIndex, prevStep?.element || null);

        if (prevStep?.page && tourPagesDiffer(currentStep?.page, prevStep?.page)) {
          await navigateTourPageChange(currentStep?.page, prevStep.page, onNavigateToPage, {
            isCancelled,
          });
          if (isCancelled()) return;
        }

        const prepared = await prepareTourStep(prevStep, { isCancelled });
        if (isCancelled()) return;
        if (prevStep?.element && !prepared) {
          let skipTo = prevIndex - 1;
          while (skipTo >= 0) {
            if (isCancelled()) return;
            const candidate = steps[skipTo];
            if (candidate?.page) {
              await navigateTourPageChange(
                prevStep?.page || currentStep?.page,
                candidate.page,
                onNavigateToPage,
                { isCancelled }
              );
              if (isCancelled()) return;
            }
            const ok = await prepareTourStep(candidate, { isCancelled });
            if (isCancelled()) return;
            if (!candidate?.element || ok) {
              persistProgress(progressStatus(), skipTo, candidate?.element || null);
              driveTo(skipTo);
              return;
            }
            skipTo -= 1;
          }
          return;
        }

        driveTo(prevIndex);
      } finally {
        navBusy = false;
        setNavButtonsBusy(false);
      }
    },
    onHighlighted: (_element, _step, { state } = {}) => {
      if (!driverRef.current) return;
      const activeIndex =
        state?.activeIndex ?? driverRef.current?.getActiveIndex?.() ?? 0;
      const activeStep = steps[activeIndex];
      persistProgress(progressStatus(), activeIndex, activeStep?.element || null);
    },
    onCloseClick: () => {
      const tourDriver = driverRef.current;
      const activeIndex = tourDriver?.getActiveIndex?.() || 0;
      persistProgress('dismissed', activeIndex, steps[activeIndex]?.element || null);
      // Invalidate in-flight Next/Back before destroy so they cannot recreate the overlay.
      hardStopTour(tourDriver);
    },
    onDoneClick: () => {
      const tourDriver = driverRef.current;
      const activeIndex = tourDriver?.getActiveIndex?.() || steps.length - 1;
      completeTour('completed', activeIndex, steps[activeIndex]?.element || null);
      hardStopTour(tourDriver);
    },
    onPopoverRender: (popover) => {
      popover.closeButton.setAttribute('aria-label', 'Skip dashboard tour');
      popover.closeButton.setAttribute('title', 'Skip tour');
      popover.closeButton.textContent = '×';
      popover.closeButton.style.pointerEvents = 'auto';
      popover.closeButton.disabled = false;

      const activeIndex = driverRef.current?.getActiveIndex?.() ?? 0;
      const activeStep = steps[activeIndex];
      const nextSectionId = activeStep?.nextSectionId || null;
      const isHandoffStep =
        activeStep?.tourStepId === 'overview-help' || activeStep?.tourStepId === 'section-handoff';
      const showNextSectionCta =
        Boolean(nextSectionId) &&
        typeof onRequestNextSection === 'function' &&
        isHandoffStep;

      const nav = popover.footer?.querySelector?.('.driver-popover-navigation-btns') || popover.footerButtons;
      nav?.querySelectorAll?.('.driver-popover-extended-btn, .driver-popover-menu-btn').forEach((node) => {
        node.remove();
      });

      if (showNextSectionCta && nav) {
        const nextBtn = document.createElement('button');
        nextBtn.type = 'button';
        nextBtn.className = 'driver-popover-extended-btn driver-popover-footer-btn';
        nextBtn.textContent = 'Next section';
        nextBtn.setAttribute('aria-label', 'Start the next section tour');
        nextBtn.addEventListener('click', (event) => {
          event.preventDefault();
          event.stopPropagation();
          // Mark this section/overview done, but do NOT persist status=completed —
          // that races with the next section start and leaves Assets with no tour.
          if (completedOptionId && typeof onTourCompleted === 'function') {
            onTourCompleted(completedOptionId);
          } else if (completedOptionId) {
            markTourOptionCompleted(storageKey, completedOptionId);
          }
          onRequestNextSection(nextSectionId);
        });
        nav.insertBefore(nextBtn, nav.firstChild);
      }

      if (navBusy) {
        popover.previousButton?.setAttribute('disabled', 'true');
        popover.nextButton?.setAttribute('disabled', 'true');
      } else {
        if ((driverRef.current?.getActiveIndex?.() ?? 0) > 0) {
          popover.previousButton?.removeAttribute('disabled');
        }
        popover.nextButton?.removeAttribute('disabled');
      }

      const wrapper = popover.wrapper;
      if (wrapper) {
        wrapper.classList.toggle('xspectre-dashboard-tour--with-extended', Boolean(showNextSectionCta));
        wrapper.classList.toggle('xspectre-dashboard-tour--with-next-section', Boolean(showNextSectionCta));
      }
    },
    onDestroyed: () => {
      if (driverRef.current) {
        driverRef.current = null;
      }
      forceRemoveDriverDom();
      // Section handoff keeps prepare chrome (sidebar) — skipping revert avoids the flash.
      if (!suppressCleanupRef?.current) {
        finishCleanup();
      }
      if (focusedBeforeTour instanceof HTMLElement && document.contains(focusedBeforeTour)) {
        focusedBeforeTour.focus();
      }
    },
  };
}

export function useDashboardTour({
  activePage,
  identity,
  isReady,
  isNarrowViewport,
  scansPageTab = 'web',
  onNavigateToDashboard,
  onNavigateToPage,
  onPrepare,
}) {
  const driverRef = useRef(null);
  const generationRef = useRef(0);
  const cleanupRef = useRef(null);
  const onPrepareRef = useRef(onPrepare);
  const onNavigateToPageRef = useRef(onNavigateToPage);
  const autoCheckedKeyRef = useRef('');
  const pendingStartRef = useRef(null);
  const startTimerRef = useRef(null);
  const syncTimerRef = useRef(null);
  const offeredPromptRef = useRef('');
  /** True while Next-section handoff owns start — blocks the React start effect from racing it. */
  const handoffLaunchRef = useRef(false);
  /** Skip prepare revert during Next-section destroy so the sidebar does not flicker. */
  const suppressCleanupRef = useRef(false);
  const requestNextSectionRef = useRef(null);
  const handleTourCompletedRef = useRef(null);
  const [startRequest, setStartRequest] = useState(0);
  const [tourSettled, setTourSettled] = useState(false);
  const [progressReady, setProgressReady] = useState(false);
  const [completedIds, setCompletedIds] = useState([]);
  const [pageTourPrompt, setPageTourPrompt] = useState(null);

  const storageKey = useMemo(() => getDashboardTourStorageKey(identity), [identity]);

  const hardDestroyTour = useCallback((options = {}) => {
    const preservePending = Boolean(options?.preservePending);
    const preserveHandoff = Boolean(options?.preserveHandoff);
    const preservePrepare = Boolean(options?.preservePrepare);
    generationRef.current += 1;
    if (!preservePending) {
      pendingStartRef.current = null;
    }
    if (!preserveHandoff) {
      handoffLaunchRef.current = false;
      removeTourHandoffVeil();
    }
    if (startTimerRef.current) {
      window.clearTimeout(startTimerRef.current);
      startTimerRef.current = null;
    }
    try {
      driverRef.current?.destroy?.();
    } catch (_) {
      /* ignore */
    }
    driverRef.current = null;
    forceRemoveDriverDom();
    if (!preservePrepare) {
      if (typeof cleanupRef.current === 'function') {
        try {
          cleanupRef.current();
        } catch (_) {
          /* ignore */
        }
      }
      cleanupRef.current = null;
    }
  }, []);

  const navigateToPage = useCallback(
    (page) => {
      if (page === 'riskDetail') {
        // riskDetail is id-specific; open the first list row instead of pageToPath('riskDetail').
        if (typeof window !== 'undefined') {
          window.dispatchEvent(new CustomEvent('xspectre:tour-open-first-risk'));
        }
        return;
      }
      if (page === 'reportDetail') {
        if (typeof window !== 'undefined') {
          window.dispatchEvent(new CustomEvent('xspectre:tour-open-first-report'));
        }
        return;
      }
      if (typeof onNavigateToPageRef.current === 'function') {
        onNavigateToPageRef.current(page);
        return;
      }
      if (page === 'dashboard') onNavigateToDashboard?.();
    },
    [onNavigateToDashboard]
  );

  useEffect(() => {
    setDashboardTourProgressSync((key, progress) => {
      if (key !== storageKey) return;
      if (progress?.status === 'completed' || progress?.status === 'dismissed') {
        setTourSettled(true);
      } else if (shouldAutoStartDashboardTour(progress)) {
        setTourSettled(false);
      }
      if (syncTimerRef.current) window.clearTimeout(syncTimerRef.current);
      syncTimerRef.current = window.setTimeout(() => {
        saveTourProgress(DASHBOARD_TOUR_ID, progress).catch(() => {});
      }, 250);
    });
    return () => {
      setDashboardTourProgressSync(null);
      if (syncTimerRef.current) window.clearTimeout(syncTimerRef.current);
    };
  }, [storageKey]);

  useEffect(() => {
    let cancelled = false;
    setProgressReady(false);
    setTourSettled(false);
    autoCheckedKeyRef.current = '';

    (async () => {
      const remoteResult = await fetchTourProgress(DASHBOARD_TOUR_ID);
      if (cancelled || pendingStartRef.current) {
        if (!cancelled) {
          setProgressReady(true);
          const progress = readDashboardTourProgress(storageKey);
          setCompletedIds(progress?.completedIds || []);
          setTourSettled(!shouldAutoStartDashboardTour(progress));
        }
        return;
      }
      const local = readDashboardTourProgress(storageKey);
      if (!remoteResult.ok) {
        setProgressReady(true);
        setCompletedIds(local?.completedIds || []);
        setTourSettled(!shouldAutoStartDashboardTour(readDashboardTourProgress(storageKey)));
        return;
      }
      const remote = normalizeRemoteTourProgress(remoteResult.progress);
      const chosen = chooseTourProgress(local, remote);
      if (chosen && chosen !== local) {
        writeDashboardTourProgress(storageKey, chosen.status, chosen.step, chosen.mode, chosen.element, {
          sync: false,
          updatedAt: chosen.updatedAt,
          sectionId: chosen.sectionId,
          completedIds: chosen.completedIds,
          promptDismissedIds: chosen.promptDismissedIds,
        });
      } else if (local && chosen === local) {
        const localTime = Date.parse(local.updatedAt || '') || 0;
        const remoteTime = Date.parse(remote?.updatedAt || '') || 0;
        if (!remote || localTime > remoteTime) {
          saveTourProgress(DASHBOARD_TOUR_ID, local).catch(() => {});
        }
      }
      setProgressReady(true);
      if (!cancelled) {
        const progress = readDashboardTourProgress(storageKey);
        setCompletedIds(progress?.completedIds || []);
        setTourSettled(!shouldAutoStartDashboardTour(progress));
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [storageKey]);

  useEffect(() => {
    onPrepareRef.current = onPrepare;
  }, [onPrepare]);

  useEffect(() => {
    onNavigateToPageRef.current = onNavigateToPage;
  }, [onNavigateToPage]);

  const refreshCompletedIds = useCallback(() => {
    const progress = readDashboardTourProgress(storageKey);
    setCompletedIds(progress?.completedIds || []);
    return progress;
  }, [storageKey]);

  const handleTourCompleted = useCallback(
    (optionId) => {
      markTourOptionCompleted(storageKey, optionId);
      refreshCompletedIds();
      setPageTourPrompt((current) => (current?.sectionId === optionId ? null : current));
    },
    [refreshCompletedIds, storageKey]
  );

  // Idle / manual logout must tear down driver.js chrome mounted on document.body.
  useEffect(() => {
    const onAuthLogout = () => {
      hardDestroyTour();
    };
    window.addEventListener(TOUR_AUTH_LOGOUT_EVENT, onAuthLogout);
    return () => window.removeEventListener(TOUR_AUTH_LOGOUT_EVENT, onAuthLogout);
  }, [hardDestroyTour]);

  const finishCleanup = useCallback(() => {
    if (typeof cleanupRef.current === 'function') cleanupRef.current();
    cleanupRef.current = null;
  }, []);

  /**
   * Imperatively mount a driver for a pending/section start.
   * Used by Next-section handoff so React effect cleanup cannot cancel the launch.
   */
  const driveTourLaunch = useCallback(
    async ({
      restart = true,
      mode = TOUR_MODE_SECTION,
      sectionId = null,
      essentials = false,
      startGeneration,
      animate = null,
    }) => {
      const isCancelled = () => generationRef.current !== startGeneration;
      const tourMode =
        mode === TOUR_MODE_SECTION || mode === TOUR_MODE_EXTENDED
          ? TOUR_MODE_SECTION
          : TOUR_MODE_OVERVIEW;
      const resolvedSectionId =
        tourMode === TOUR_MODE_SECTION
          ? sectionId || getFirstTourSectionId({ isNarrowViewport })
          : null;

      const priorProgress = readDashboardTourProgress(storageKey);
      const requestedStep = restart ? 0 : priorProgress?.step || 0;
      const rawSteps =
        tourMode === TOUR_MODE_SECTION
          ? buildSectionTourSteps(resolvedSectionId, { isNarrowViewport, essentials })
          : buildDashboardTourSteps({ isNarrowViewport });
      if (!rawSteps.length) return false;

      const resumeByElement =
        !restart && priorProgress?.element
          ? rawSteps.find((step) => step.element === priorProgress.element)
          : null;
      const resumeStep =
        resumeByElement || rawSteps[Math.min(requestedStep, Math.max(rawSteps.length - 1, 0))];

      if (resumeStep?.page) {
        navigateToPage(resumeStep.page);
        await waitForTourPage(resumeStep.page);
        if (isCancelled()) return false;
        if (resumeStep.element) {
          await waitForSelector(resumeStep.element, { timeoutMs: 8000, isCancelled });
        }
        if (isCancelled()) return false;
        await delay(animate === false ? 40 : 120);
      }

      if (isCancelled()) return false;
      await prepareTourStep(resumeStep, { isCancelled });
      if (isCancelled()) return false;

      // Prefer the step's page once DOM is ready — React activePage can lag the route.
      const pageForFilter = resumeStep?.page || activePage;

      let steps = rawSteps.filter((step) => shouldKeepTourStep(step, { activePage: pageForFilter }));
      // Never abort a section handoff because row-dependent steps are not ready yet.
      if (steps.length === 0) {
        steps = rawSteps.filter((step) => step?.element || step?.tourStepId === 'section-handoff');
      }
      if (steps.length === 0) return false;

      let startAt = 0;
      if (resumeStep?.element) {
        const idx = steps.findIndex((step) => step.element === resumeStep.element);
        startAt = idx >= 0 ? idx : Math.min(requestedStep, Math.max(steps.length - 1, 0));
      } else {
        startAt = Math.min(requestedStep, Math.max(steps.length - 1, 0));
      }

      const startStep = steps[startAt];
      await prepareTourStep(startStep, { isCancelled });
      if (isCancelled()) return false;
      if (startStep?.element) {
        const ready = await waitForSelector(startStep.element, { timeoutMs: 8000, isCancelled });
        if (isCancelled()) return false;
        if (!ready) return false;
      }

      try {
        driverRef.current?.destroy?.();
      } catch (_) {
        /* ignore */
      }
      forceRemoveDriverDom();
      if (isCancelled()) return false;

      const focusedBeforeTour = document.activeElement;
      if (!cleanupRef.current) {
        cleanupRef.current = onPrepareRef.current?.() || null;
      }

      const tour = driver(
        createTourDriverConfig({
          steps,
          storageKey,
          mode: tourMode,
          sectionId: resolvedSectionId,
          focusedBeforeTour,
          finishCleanup,
          driverRef,
          generationRef,
          onNavigateToPage: navigateToPage,
          onRequestNextSection: (id) => requestNextSectionRef.current?.(id),
          onTourCompleted: (id) => handleTourCompletedRef.current?.(id),
          animate,
          suppressCleanupRef,
        })
      );

      if (isCancelled()) {
        try {
          tour.destroy();
        } catch (_) {
          /* ignore */
        }
        forceRemoveDriverDom();
        return false;
      }

      pendingStartRef.current = null;
      driverRef.current = tour;
      tour.drive(startAt);
      return true;
    },
    [activePage, finishCleanup, isNarrowViewport, navigateToPage, storageKey]
  );

  const requestStart = useCallback(
    ({
      restart = false,
      mode = TOUR_MODE_OVERVIEW,
      sectionId = null,
      startElement = null,
      startPage = null,
      essentials = false,
    } = {}) => {
      setPageTourPrompt(null);
      const wantsSection =
        mode === TOUR_MODE_SECTION || mode === TOUR_MODE_EXTENDED || Boolean(sectionId);
      const resolvedSectionId = wantsSection
        ? sectionId || getFirstTourSectionId({ isNarrowViewport })
        : null;
      const nextMode = wantsSection ? TOUR_MODE_SECTION : TOUR_MODE_OVERVIEW;
      const steps = wantsSection
        ? buildSectionTourSteps(resolvedSectionId, { isNarrowViewport, essentials })
        : buildDashboardTourSteps({ isNarrowViewport });

      let stepIndex = 0;
      let element = null;
      if (restart || startElement || startPage || wantsSection) {
        if (!wantsSection && startElement) {
          const idx = steps.findIndex((step) => step.element === startElement);
          stepIndex = idx >= 0 ? idx : 0;
        } else if (!wantsSection && startPage) {
          const idx = steps.findIndex((step) => step.page === startPage);
          stepIndex = idx >= 0 ? idx : 0;
        } else {
          stepIndex = 0;
        }
        element = steps[stepIndex]?.element || null;
        writeDashboardTourProgress(
          storageKey,
          wantsSection ? 'section_in_progress' : 'in_progress',
          stepIndex,
          nextMode,
          element,
          { sectionId: resolvedSectionId }
        );
      }

      pendingStartRef.current = {
        restart: Boolean(restart),
        mode: nextMode,
        sectionId: resolvedSectionId,
        essentials: Boolean(essentials),
      };
      if (wantsSection) {
        const section = getTourSectionDef(resolvedSectionId);
        const page =
          startPage ||
          section?.startPage ||
          steps[stepIndex]?.page ||
          'targets';
        navigateToPage(page);
      } else if (activePage !== 'dashboard') {
        onNavigateToDashboard?.();
      }
      setStartRequest((value) => value + 1);
    },
    [activePage, isNarrowViewport, navigateToPage, onNavigateToDashboard, storageKey]
  );

  const requestNextSection = useCallback(
    (sectionId) => {
      const nextId = sectionId || getFirstTourSectionId({ isNarrowViewport });
      if (!nextId) return;
      const section = getTourSectionDef(nextId);
      const steps = buildSectionTourSteps(nextId, { isNarrowViewport });
      const startElement = steps[0]?.element || section?.startElement || null;
      const page = section?.startPage || steps[0]?.page || 'targets';

      // Persist before tear-down so a failed drive can still recover from storage.
      writeDashboardTourProgress(
        storageKey,
        'section_in_progress',
        0,
        TOUR_MODE_SECTION,
        startElement,
        { sectionId: nextId }
      );

      handoffLaunchRef.current = true;
      pendingStartRef.current = {
        restart: true,
        mode: TOUR_MODE_SECTION,
        sectionId: nextId,
        essentials: false,
      };

      // Dim stays up across destroy + navigate so Next section does not flash.
      mountTourHandoffVeil();
      suppressCleanupRef.current = true;
      hardDestroyTour({
        preservePending: true,
        preserveHandoff: true,
        preservePrepare: true,
      });
      // Keep existing prepare chrome; only open if somehow missing.
      if (!cleanupRef.current) {
        cleanupRef.current = onPrepareRef.current?.() || cleanupRef.current;
      }
      navigateToPage(page);

      const startGeneration = generationRef.current + 1;
      generationRef.current = startGeneration;

      void (async () => {
        try {
          await delay(80);
          if (generationRef.current !== startGeneration) return;

          let launched = await driveTourLaunch({
            restart: true,
            mode: TOUR_MODE_SECTION,
            sectionId: nextId,
            essentials: false,
            startGeneration,
            animate: false,
          });

          // One retry after tables remount (scans/assets).
          if (!launched && generationRef.current === startGeneration) {
            await delay(400);
            if (generationRef.current !== startGeneration) return;
            launched = await driveTourLaunch({
              restart: true,
              mode: TOUR_MODE_SECTION,
              sectionId: nextId,
              essentials: false,
              startGeneration,
              animate: false,
            });
          }

          if (!launched && generationRef.current === startGeneration) {
            pendingStartRef.current = {
              restart: true,
              mode: TOUR_MODE_SECTION,
              sectionId: nextId,
              essentials: false,
            };
            setStartRequest((value) => value + 1);
          }

          // Let the new driver paint under the veil, then lift it.
          await delay(40);
        } finally {
          removeTourHandoffVeil();
          suppressCleanupRef.current = false;
          if (generationRef.current === startGeneration) {
            handoffLaunchRef.current = false;
          }
        }
      })();
    },
    [driveTourLaunch, hardDestroyTour, isNarrowViewport, navigateToPage, storageKey]
  );
  useEffect(() => {
    requestNextSectionRef.current = requestNextSection;
  }, [requestNextSection]);

  useEffect(() => {
    handleTourCompletedRef.current = handleTourCompleted;
  }, [handleTourCompleted]);

  const startTourFrom = useCallback(
    (optionOrId) => {
      hardDestroyTour();
      cleanupRef.current = onPrepareRef.current?.() || cleanupRef.current;
      const option =
        typeof optionOrId === 'string'
          ? TOUR_START_OPTIONS.find((item) => item.id === optionOrId) ||
            getTourSectionDef(optionOrId)
          : optionOrId;
      if (!option) {
        requestStart({ restart: true, mode: TOUR_MODE_OVERVIEW });
        return;
      }
      if (option.id === 'overview' || option.mode === TOUR_MODE_OVERVIEW) {
        requestStart({ restart: true, mode: TOUR_MODE_OVERVIEW });
        return;
      }
      requestStart({
        restart: true,
        mode: TOUR_MODE_SECTION,
        sectionId: option.id,
        startPage: option.startPage || null,
      });
    },
    [hardDestroyTour, requestStart]
  );
  useEffect(() => {
    if (!isReady || !progressReady) {
      setPageTourPrompt(null);
      return undefined;
    }
    if (
      handoffLaunchRef.current ||
      pendingStartRef.current ||
      driverRef.current?.isActive?.()
    ) {
      setPageTourPrompt(null);
      return undefined;
    }

    const sectionId = resolveTourSectionIdForPage(activePage, { scansPageTab });
    if (!sectionId || sectionId === 'overview') {
      setPageTourPrompt(null);
      offeredPromptRef.current = '';
      return undefined;
    }

    const progress = readDashboardTourProgress(storageKey);
    if (!shouldOfferPageTourPrompt(progress, sectionId)) {
      setPageTourPrompt(null);
      offeredPromptRef.current = sectionId;
      return undefined;
    }

    const offerKey = `${storageKey}:${sectionId}`;
    if (offeredPromptRef.current === offerKey && pageTourPrompt?.sectionId === sectionId) {
      return undefined;
    }

    const section = getTourSectionDef(sectionId) || TOUR_START_OPTIONS.find((item) => item.id === sectionId);
    const timer = window.setTimeout(() => {
      offeredPromptRef.current = offerKey;
      setPageTourPrompt({
        sectionId,
        label: section?.label || sectionId,
        description: section?.description || 'A short walkthrough of this page.',
      });
    }, 700);

    return () => window.clearTimeout(timer);
  }, [
    activePage,
    isReady,
    pageTourPrompt?.sectionId,
    progressReady,
    scansPageTab,
    startRequest,
    storageKey,
    completedIds,
  ]);

  useEffect(() => {
    if (!isReady || !progressReady) return undefined;
    if (autoCheckedKeyRef.current === storageKey) return undefined;
    const progress = readDashboardTourProgress(storageKey);
    if (!shouldAutoStartDashboardTour(progress)) {
      autoCheckedKeyRef.current = storageKey;
      return undefined;
    }

    const wantsSection =
      progress?.status === 'section_in_progress' ||
      progress?.status === 'extended_in_progress' ||
      progress?.mode === TOUR_MODE_SECTION ||
      progress?.mode === TOUR_MODE_EXTENDED;
    const mode = wantsSection ? TOUR_MODE_SECTION : TOUR_MODE_OVERVIEW;
    const sectionId = wantsSection
      ? progress?.sectionId || getFirstTourSectionId({ isNarrowViewport })
      : null;

    if (mode === TOUR_MODE_OVERVIEW && activePage !== 'dashboard') return undefined;

    const timer = window.setTimeout(() => {
      autoCheckedKeyRef.current = storageKey;
      pendingStartRef.current = { restart: false, mode, sectionId };
      if (mode === TOUR_MODE_SECTION) {
        const steps = buildSectionTourSteps(sectionId, { isNarrowViewport });
        const stepIndex = Math.min(progress?.step || 0, Math.max(steps.length - 1, 0));
        navigateToPage(steps[stepIndex]?.page || getTourSectionDef(sectionId)?.startPage || 'targets');
      }
      setStartRequest((value) => value + 1);
    }, 0);
    return () => window.clearTimeout(timer);
  }, [activePage, isNarrowViewport, isReady, navigateToPage, progressReady, storageKey]);

  useEffect(() => {
    if (!isReady || !pendingStartRef.current) return undefined;
    // Next-section handoff drives imperatively — stay out of its way.
    if (handoffLaunchRef.current) return undefined;

    const { restart, mode, sectionId: pendingSectionId, essentials = false } = pendingStartRef.current;
    const tourMode =
      mode === TOUR_MODE_SECTION || mode === TOUR_MODE_EXTENDED
        ? TOUR_MODE_SECTION
        : TOUR_MODE_OVERVIEW;
    const sectionId =
      tourMode === TOUR_MODE_SECTION
        ? pendingSectionId || getFirstTourSectionId({ isNarrowViewport })
        : null;
    if (tourMode === TOUR_MODE_OVERVIEW && activePage !== 'dashboard') return undefined;

    const priorProgress = readDashboardTourProgress(storageKey);
    const requestedStep = restart ? 0 : priorProgress?.step || 0;
    const rawSteps =
      tourMode === TOUR_MODE_SECTION
        ? buildSectionTourSteps(sectionId, { isNarrowViewport, essentials })
        : buildDashboardTourSteps({ isNarrowViewport });
    const resumeByElement =
      !restart && priorProgress?.element
        ? rawSteps.find((step) => step.element === priorProgress.element)
        : null;
    const resumeStep =
      resumeByElement || rawSteps[Math.min(requestedStep, Math.max(rawSteps.length - 1, 0))];

    // Section tours remount pages — wait until the target page is active.
    if (tourMode === TOUR_MODE_SECTION && resumeStep?.page) {
      if (!tourPageMatchesActive(resumeStep.page, activePage)) {
        navigateToPage(resumeStep.page);
        return undefined;
      }
    }

    const focusedBeforeTour = document.activeElement;

    if (!cleanupRef.current) {
      cleanupRef.current = onPrepareRef.current?.() || null;
    }

    const startGeneration = generationRef.current + 1;
    generationRef.current = startGeneration;
    const isCancelled = () => generationRef.current !== startGeneration;
    const pendingSnapshot = { ...pendingStartRef.current };

    startTimerRef.current = window.setTimeout(async () => {
      if (isCancelled()) return;

      if (resumeStep?.page) {
        navigateToPage(resumeStep.page);
        await waitForTourPage(resumeStep.page, { isCancelled });
        if (isCancelled()) {
          // Keep the handoff alive so a remount/re-render can finish starting.
          if (!driverRef.current && !pendingStartRef.current) {
            pendingStartRef.current = pendingSnapshot;
          }
          return;
        }
        if (resumeStep.element) {
          await waitForSelector(resumeStep.element, { timeoutMs: 8000, isCancelled });
        }
        if (isCancelled()) {
          if (!driverRef.current && !pendingStartRef.current) {
            pendingStartRef.current = pendingSnapshot;
          }
          return;
        }
        await delay(220);
      }

      if (isCancelled()) {
        if (!driverRef.current && !pendingStartRef.current) {
          pendingStartRef.current = pendingSnapshot;
        }
        return;
      }
      await prepareTourStep(resumeStep, { isCancelled });
      if (isCancelled()) {
        if (!driverRef.current && !pendingStartRef.current) {
          pendingStartRef.current = pendingSnapshot;
        }
        return;
      }

      const steps = rawSteps.filter((step) => shouldKeepTourStep(step, { activePage }));

      if (steps.length === 0) {
        // Keep pending and nudge another attempt — clearing here left Web Scans stuck.
        if (!pendingStartRef.current && pendingSnapshot) {
          pendingStartRef.current = pendingSnapshot;
        }
        startTimerRef.current = window.setTimeout(() => {
          if (!driverRef.current && pendingStartRef.current) {
            setStartRequest((value) => value + 1);
          }
        }, 400);
        return;
      }

      // Resume against the filtered list using the persisted step's element when possible.
      let startAt = 0;
      if (resumeStep?.element) {
        const idx = steps.findIndex((step) => step.element === resumeStep.element);
        startAt = idx >= 0 ? idx : Math.min(requestedStep, Math.max(steps.length - 1, 0));
      } else {
        startAt = Math.min(requestedStep, Math.max(steps.length - 1, 0));
      }

      const startStep = steps[startAt];
      await prepareTourStep(startStep, { isCancelled });
      if (isCancelled()) {
        if (!driverRef.current && !pendingStartRef.current) {
          pendingStartRef.current = pendingSnapshot;
        }
        return;
      }

      try {
        driverRef.current?.destroy?.();
      } catch (_) {
        /* ignore */
      }
      forceRemoveDriverDom();
      if (isCancelled()) {
        if (!driverRef.current && !pendingStartRef.current) {
          pendingStartRef.current = pendingSnapshot;
        }
        return;
      }

      const tour = driver(
        createTourDriverConfig({
          steps,
          storageKey,
          mode: tourMode,
          sectionId,
          focusedBeforeTour,
          finishCleanup,
          driverRef,
          generationRef,
          onNavigateToPage: navigateToPage,
          onRequestNextSection: (id) => requestNextSectionRef.current?.(id),
          onTourCompleted: (id) => handleTourCompletedRef.current?.(id),
          suppressCleanupRef,
        })
      );

      if (isCancelled()) {
        try {
          tour.destroy();
        } catch (_) {
          /* ignore */
        }
        forceRemoveDriverDom();
        if (!driverRef.current && !pendingStartRef.current) {
          pendingStartRef.current = pendingSnapshot;
        }
        return;
      }

      // Consume pending only after the driver is live — earlier clears raced Next section.
      pendingStartRef.current = null;
      driverRef.current = tour;
      tour.drive(startAt);
    }, 280);

    return () => {
      if (startTimerRef.current) {
        window.clearTimeout(startTimerRef.current);
        startTimerRef.current = null;
      }
      // Only cancel in-flight async work. Never invalidate a tour that already drove.
      if (generationRef.current === startGeneration && !driverRef.current) {
        generationRef.current += 1;
        if (!pendingStartRef.current && pendingSnapshot) {
          pendingStartRef.current = pendingSnapshot;
        }
      }
    };
  }, [
    activePage,
    finishCleanup,
    isNarrowViewport,
    isReady,
    navigateToPage,
    startRequest,
    storageKey,
  ]);

  // Recover stuck section_in_progress (progress written, driver never mounted).
  useEffect(() => {
    if (!isReady || !progressReady) return undefined;
    if (handoffLaunchRef.current || pendingStartRef.current) return undefined;
    if (driverRef.current?.isActive?.()) return undefined;

    const progress = readDashboardTourProgress(storageKey);
    if (progress?.status !== 'section_in_progress' || !progress.sectionId) return undefined;

    const section = getTourSectionDef(progress.sectionId);
    if (!section) return undefined;

    if (!tourPageMatchesActive(section.startPage, activePage)) {
      const timer = window.setTimeout(() => {
        if (handoffLaunchRef.current || driverRef.current?.isActive?.()) return;
        navigateToPage(section.startPage);
      }, 200);
      return () => window.clearTimeout(timer);
    }

    const timer = window.setTimeout(() => {
      if (handoffLaunchRef.current || pendingStartRef.current || driverRef.current?.isActive?.()) {
        return;
      }
      pendingStartRef.current = {
        restart: true,
        mode: TOUR_MODE_SECTION,
        sectionId: progress.sectionId,
        essentials: false,
      };
      setStartRequest((value) => value + 1);
    }, 350);
    return () => window.clearTimeout(timer);
  }, [activePage, isReady, navigateToPage, progressReady, startRequest, storageKey]);

  useEffect(
    () => () => {
      hardDestroyTour();
    },
    [hardDestroyTour]
  );

  const acceptPageTourPrompt = useCallback(() => {
    const sectionId = pageTourPrompt?.sectionId;
    if (!sectionId) return;
    setPageTourPrompt(null);
    hardDestroyTour();
    cleanupRef.current = onPrepareRef.current?.() || cleanupRef.current;
    requestStart({
      restart: true,
      mode: TOUR_MODE_SECTION,
      sectionId,
      essentials: true,
    });
  }, [hardDestroyTour, pageTourPrompt?.sectionId, requestStart]);

  const dismissPageTourPrompt = useCallback(() => {
    const sectionId = pageTourPrompt?.sectionId;
    if (sectionId) {
      dismissTourPagePrompt(storageKey, sectionId);
    }
    setPageTourPrompt(null);
  }, [pageTourPrompt?.sectionId, storageKey]);

  const tourStartOptions = useMemo(() => {
    const visibleSections = new Set(
      listTourSections({ isNarrowViewport }).map((section) => section.id)
    );
    return TOUR_START_OPTIONS.filter((option) => {
      if (option.id === 'overview') return true;
      return visibleSections.has(option.id);
    }).map((option) => ({
      ...option,
      completed: completedIds.includes(option.id),
    }));
  }, [completedIds, isNarrowViewport]);

  const completedCount = tourStartOptions.filter((option) => option.completed).length;

  return {
    startDashboardTour: () => {
      hardDestroyTour();
      requestStart({ restart: true, mode: TOUR_MODE_OVERVIEW });
    },
    startExtendedTour: () => {
      requestNextSection(getFirstTourSectionId({ isNarrowViewport }));
    },
    startNextSection: requestNextSection,
    startTourFrom,
    destroyTour: hardDestroyTour,
    tourStartOptions,
    completedIds,
    completedCount,
    pageTourPrompt,
    acceptPageTourPrompt,
    dismissPageTourPrompt,
    tourSettled,
    isDashboardTourActive: () => Boolean(driverRef.current?.isActive()),
  };
}
